import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService, agentNameToSlug } from '../services/api';
import { Settings, Save, Loader2, Plus, Edit, Trash2, Bot, HelpCircle, User, MessageCircle, MessageSquare, ShoppingCart, Phone, Mail, Info, Users, Key, Shield, Layout, Folder, ChevronLeft, ChevronRight, Moon, Sun } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';
import { resolveTranslationKey, isTranslationKey } from '../utils/translation';
import { IconPicker } from './ui/IconPicker';
import { toast } from '../hooks/use-toast';
import type { TenantIconConfig } from '../types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';

type IconName = 'MessageCircle' | 'MessageSquare' | 'ShoppingCart' | 'Settings' | 'User' | 'Bot' | 'HelpCircle' | 'Info' | 'Phone' | 'Mail' | 'Layout' | 'Folder';

const iconOptions = [
  { value: 'Bot', label: 'Bot', icon: Bot },
  { value: 'Settings', label: 'Settings', icon: Settings },
  { value: 'HelpCircle', label: 'Help', icon: HelpCircle },
  { value: 'User', label: 'User', icon: User },
  { value: 'MessageCircle', label: 'Message Circle', icon: MessageCircle },
  { value: 'MessageSquare', label: 'Message Square', icon: MessageSquare },
  { value: 'ShoppingCart', label: 'Shopping Cart', icon: ShoppingCart },
  { value: 'Phone', label: 'Phone', icon: Phone },
  { value: 'Mail', label: 'Mail', icon: Mail },
  { value: 'Info', label: 'Info', icon: Info },
  { value: 'Layout', label: 'Layout', icon: Layout },
  { value: 'Folder', label: 'Folder', icon: Folder },
];

// Draggable toolbar item component
const DraggableToolbarItem: React.FC<{
  id: string;
  children: React.ReactNode;
  className?: string;
}> = ({ id, children, className = "" }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`p-3 border border-border rounded-lg cursor-grab bg-background hover:bg-muted/50 transition-colors active:cursor-grabbing ${className}`}
    >
      {children}
    </div>
  );
};

// Draggable canvas item for re-parenting only
const DraggableCanvasItem: React.FC<{
  id: string;
  children: React.ReactNode;
  className?: string;
}> = ({ id, children, className = "" }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${className} cursor-move`}
    >
      {children}
    </div>
  );
};

// Droppable canvas component
const DroppableCanvas: React.FC<{
  id: string;
  children: React.ReactNode;
  className?: string;
}> = ({ id, children, className = "" }) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`${className} ${isOver ? 'bg-primary/10 border-primary ring-2 ring-primary ring-offset-2' : ''} transition-all duration-200`}
    >
      {children}
    </div>
  );
};

type TabType = 'application' | 'agents' | 'embedded' | 'home' | 'i18n' | 'users';

// Helper functions for color conversion
const normalizeGroupAccess = (groupAccess: any): string[] => {
  if (!groupAccess) return [];

  // Already an array
  if (Array.isArray(groupAccess)) {
    return groupAccess.filter((v) => v != null).map((v) => String(v));
  }

  // Sometimes returned from the API as a JSON string
  if (typeof groupAccess === 'string') {
    try {
      const parsed = JSON.parse(groupAccess);
      return Array.isArray(parsed) ? parsed.filter((v) => v != null).map((v) => String(v)) : [];
    } catch {
      return [];
    }
  }

  return [];
};

const hexToHSL = (hex: string): string => {
  if (!hex || hex === '') return '';
  
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);
  
  return `${h} ${s}% ${l}%`;
};

const hslToHex = (hsl: string): string => {
  if (!hsl || hsl === '') return '#000000';
  
  // Parse HSL string like "210 40% 98%"
  const parts = hsl.match(/(\d+\.?\d*)\s+(\d+\.?\d*)%\s+(\d+\.?\d*)%/);
  if (!parts) return '#000000';
  
  let h = parseFloat(parts[1]) / 360;
  const s = parseFloat(parts[2]) / 100;
  const l = parseFloat(parts[3]) / 100;
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (n: number) => {
    const hex = Math.round(n * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Color Picker Input Component
const ColorPickerInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  defaultColor?: string;
}> = ({ label, value, onChange, placeholder = 'e.g., 210 40% 98%', defaultColor = '210 40% 98%' }) => {
  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="flex gap-2">
        {value ? (
          <input
            type="color"
            value={hslToHex(value)}
            onChange={(e) => onChange(hexToHSL(e.target.value))}
            className="h-10 w-20 border border-input bg-background rounded-md cursor-pointer"
          />
        ) : (
          <button
            type="button"
            onClick={() => onChange(defaultColor)}
            className="h-10 w-20 border border-input bg-background rounded-md hover:bg-muted transition-colors text-sm font-medium"
          >
            Pick
          </button>
        )}
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export const SetupDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const notifySuccess = (title: string, description?: string) =>
    toast({ title, description });

  const notifyError = (description: string, title = 'Error') =>
    toast({ variant: 'destructive', title, description });

  // Get initial tab from URL or default to 'application'
  const getInitialTab = (): TabType => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['application', 'agents', 'embedded', 'home', 'users', 'i18n'].includes(tabParam)) {
      return tabParam as TabType;
    }
    return 'application';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab);

  // Handle tab changes and update URL
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  // Translation setup
  const { translations } = useTranslation();
  const [config, setConfig] = useState({
    pageTitle: '',
    welcomeMessage: '',
    companyName: '',
    logoBase64: '',
    logoPath: '',
    useTextLogo: false,
    logoLightBrightness: '1.0',
    logoLightContrast: '1.0',
    logoLightDropShadowOpacity: '0.0',
    logoDarkBrightness: '1.1',
    logoDarkContrast: '1.1',
    logoDarkDropShadowOpacity: '0.1',
  });

  // Icon configuration state
  const [iconConfig, setIconConfig] = useState<TenantIconConfig>({});

  // Agent management state
  const [agents, setAgents] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<any | null>(null);
  const [selectedAgentLanguage, setSelectedAgentLanguage] = useState<string>('');
  const [agentForm, setAgentForm] = useState({
    name: '',
    icon: 'Bot' as IconName,
    color: '#3b82f6',
    chatApiUrl: '',
    agentSid: '',
    agentSidSource: 'user_email' as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom',
    chatUserId: '',
    customFavicon: '',
    streamingEnabled: false,
    enablePersistence: false,
    enablePreemptiveEndChatButton: false,
    enableClearChatButton: false,
    showRouteStack: false,
    fetchForWelcomeMessage: false,
    allowNotifications: false,
    fetchForNotification: false,
    notificationPoolingFrequency: 15,
    // File upload configuration (chat input attachments)
    allowFileUpload: false,
    allowImagePaste: false,
    allowedFileTypes: '', // comma-separated extensions, e.g. "pdf,docx,png"
    maxFileSize: 10, // MB
    maxFiles: 5,
    feedbackEnabled: false,
    feedbackPositiveUrl: '',
    feedbackNegativeUrl: '',
    feedbackMinChars: 20,
    recaptchaEnabled: false,
    recaptchaSiteKey: '',
    recaptchaVersion: 'v2' as 'v2' | 'v3',
    footnoteEnabled: false,
    agentMessageFootnote: '',
    footnoteTitle: '',
    footnoteCollapsible: true,
    footnoteShowTiming: 'after_response' as 'after_response' | 'immediately' | 'let_agent_decide',
    // Light theme
    themePrimary: '',
    themeSecondary: '',
    themeBackground: '',
    themeForeground: '',
    themeCard: '',
    themeCardForeground: '',
    themePrimaryForeground: '',
    themeSecondaryForeground: '',
    themeMuted: '',
    themeMutedForeground: '',
    themeBorder: '',
    themeInput: '',
    // Dark theme
    themeDarkPrimary: '',
    themeDarkSecondary: '',
    themeDarkBackground: '',
    themeDarkForeground: '',
    themeDarkCard: '',
    themeDarkCardForeground: '',
    themeDarkPrimaryForeground: '',
    themeDarkSecondaryForeground: '',
    themeDarkMuted: '',
    themeDarkMutedForeground: '',
    themeDarkBorder: '',
    themeDarkInput: '',
    isActive: true,
    isPublic: false,
    translations: {} as Record<string, {
      name: string;
      description: string;
      chatTitle: string;
      welcomeMessage: string;
    }>
  });

  // Embedded content management state
  const [embeddedContents, setEmbeddedContents] = useState<any[]>([]);
  const [embeddedContentsLoading, setEmbeddedContentsLoading] = useState(false);
  const [showEmbeddedContentForm, setShowEmbeddedContentForm] = useState(false);
  const [editingEmbeddedContent, setEditingEmbeddedContent] = useState<any | null>(null);
  const [embeddedContentForm, setEmbeddedContentForm] = useState({
    name: '',
    description: '',
    icon: 'Bot' as IconName,
    color: '#3b82f6',
    embeddingUrl: '',
    title: '',
    isActive: true,
  });

  // Home screen management state
  const [homeScreenItems, setHomeScreenItems] = useState<any[]>([]);

  // Drag overlay state
  const [activeId, setActiveId] = useState<string | null>(null);

  // Drag and drop sensors (must be called at top level, not conditionally)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  // i18n management state
  const [languages, setLanguages] = useState<any[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [translationList, setTranslationList] = useState<any[]>([]);
  const [showLanguageForm, setShowLanguageForm] = useState(false);
  const [showTranslationForm, setShowTranslationForm] = useState(false);
  const [languageForm, setLanguageForm] = useState({
    code: '',
    name: '',
    nativeName: '',
    isActive: true,
    isDefault: false,
  });
  const [translationForm, setTranslationForm] = useState({
    key: '',
    value: '',
    context: '',
  });

  // User management state
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    isActive: true,
  });
  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // User groups management state
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [userGroupsLoading, setUserGroupsLoading] = useState(false);
  const [showUserGroupForm, setShowUserGroupForm] = useState(false);
  const [editingUserGroup, setEditingUserGroup] = useState<any | null>(null);
  const [userGroupForm, setUserGroupForm] = useState({
    name: '',
    description: '',
    userIds: [] as string[],
  });

  // Home screen group editing state
  const [showGroupEditForm, setShowGroupEditForm] = useState(false);
  const [editingHomeScreenGroup, setEditingHomeScreenGroup] = useState<any | null>(null);
  const [groupEditForm, setGroupEditForm] = useState({
    name: '',
    showTitle: true,
    groupAccess: [] as string[],
  });
  const [openidConfig, setOpenidConfig] = useState({
    enabled: false,
    providerName: '',
    logoUrl: '',
    clientId: '',
    clientSecret: '',
    authority: '',
    authorizeUrl: '',
    tokenUrl: '',
    profileUrl: '',
    scope: 'openid profile email',
    jsonPathEmail: 'email',
    jsonPathName: 'name',
    jsonPathOidcUserId: 'sub',
    redirectUri: '',
    buttonTemplate: '',
    buttonClasses: '',
    buttonImageUrl: '',
  });
  const [callbackUrl, setCallbackUrl] = useState<string>('');
  const [localLoginEnabled, setLocalLoginEnabled] = useState(true); // Default to enabled

  // Load current configuration and agents
  useEffect(() => {
    const loadData = async () => {
      const user = apiService.getStoredUser();
      if (!user?.tenantId) return;

      try {
        setLoading(true);

        // Load tenant config
        const tenantConfig = await apiService.getTenantConfig(user.tenantId);
        setConfig({
          pageTitle: tenantConfig.pageTitle || 'app.title',
          welcomeMessage: tenantConfig.welcomeMessage || 'app.welcomeMessage',
          companyName: tenantConfig.companyName || '',
          logoBase64: tenantConfig.logoBase64 || '',
          logoPath: tenantConfig.logoPath || '',
          useTextLogo: tenantConfig.useTextLogo ?? false,
          logoLightBrightness: tenantConfig.logoLightBrightness || '1.0',
          logoLightContrast: tenantConfig.logoLightContrast || '1.0',
          logoLightDropShadowOpacity: tenantConfig.logoLightDropShadowOpacity || '0.0',
          logoDarkBrightness: tenantConfig.logoDarkBrightness || '1.1',
          logoDarkContrast: tenantConfig.logoDarkContrast || '1.1',
          logoDarkDropShadowOpacity: tenantConfig.logoDarkDropShadowOpacity || '0.1',
        });

        // Load icon configuration
        if (tenantConfig.iconConfig) {
          try {
            const parsed = typeof tenantConfig.iconConfig === 'string'
              ? JSON.parse(tenantConfig.iconConfig)
              : tenantConfig.iconConfig;
            setIconConfig(parsed || {});
          } catch (err) {
            console.error('Failed to parse iconConfig:', err);
            setIconConfig({});
          }
        } else {
          setIconConfig({});
        }

        // Load local login setting
        setLocalLoginEnabled(tenantConfig.localLoginEnabled ?? true);

        // Load OpenID Connect configuration early so Agent Management has the correct SID source options.
        // (Previously this was only loaded when visiting the User Management tab.)
        await loadAuthConfig();

        // Load agents
        await loadAgents();

        // Load embedded contents
        await loadEmbeddedContents();

        // Load home screen items
        await loadHomeScreenItems();

        // Load languages
        await loadLanguages();
      } catch (err) {
        console.error('Failed to load data:', err);
        notifyError('Failed to load current configuration');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Clean up orphaned references when agents or embedded contents change
  useEffect(() => {
    const agentIds = new Set(agents.map(a => a.id));
    const embeddedIds = new Set(embeddedContents.map(e => e.id));

    // Remove home screen items that reference non-existent agents or embedded content
    setHomeScreenItems(prev => prev.filter(item => {
      if (item.type === 'agent' && item.agentId && !agentIds.has(item.agentId)) {
        console.log('Removing orphaned agent reference:', item.agentId);
        return false;
      }
      if (item.type === 'embedded' && item.embeddedContentId && !embeddedIds.has(item.embeddedContentId)) {
        console.log('Removing orphaned embedded content reference:', item.embeddedContentId);
        return false;
      }
      return true;
    }));
  }, [agents, embeddedContents]);

  const loadAgents = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setAgentsLoading(true);
      const tenantAgents = await apiService.getTenantAgents(user.tenantId);
      setAgents(tenantAgents);
    } catch (err) {
      console.error('Failed to load agents:', err);
      notifyError('Failed to load agents');
    } finally {
      setAgentsLoading(false);
    }
  };

  const loadEmbeddedContents = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setEmbeddedContentsLoading(true);
      const tenantEmbeddedContents = await apiService.getTenantEmbeddedContents(user.tenantId);
      setEmbeddedContents(tenantEmbeddedContents);
    } catch (err) {
      console.error('Failed to load embedded contents:', err);
      notifyError('Failed to load embedded contents');
    } finally {
      setEmbeddedContentsLoading(false);
    }
  };

  const loadHomeScreenItems = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      const tenantHomeScreenItems = await apiService.getTenantHomeScreenItems(user.tenantId);
      // Normalize groupAccess so the editor always works with string[]
      const normalized = (tenantHomeScreenItems || []).map((item: any) => ({
        ...item,
        groupAccess: normalizeGroupAccess(item.groupAccess),
      }));
      setHomeScreenItems(normalized);
    } catch (err) {
      console.error('Failed to load home screen items:', err);
      notifyError('Failed to load home screen items');
    }
  };

  const handleSave = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      await apiService.updateTenantConfig(user.tenantId, {
        ...config,
        iconConfig: iconConfig,
      });

      notifySuccess('Saved', 'Configuration saved successfully!');
    } catch (err) {
      console.error('Failed to save config:', err);
      notifyError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Agent CRUD functions
  const handleCreateAgent = () => {
    setEditingAgent(null);

    // Initialize translations for all languages
    const initialTranslations: Record<string, any> = {};
    languages.forEach(lang => {
      initialTranslations[lang.id] = {
        name: '',
        description: '',
        chatTitle: '',
        welcomeMessage: ''
      };
    });

    setAgentForm({
      name: '',
      icon: 'Bot',
      color: '#3b82f6',
      chatApiUrl: '',
      agentSid: '',
      agentSidSource: 'user_email' as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom',
      chatUserId: '',
      customFavicon: '',
      streamingEnabled: false,
      enablePersistence: false,
      enablePreemptiveEndChatButton: false,
      enableClearChatButton: false,
      showRouteStack: false,
      fetchForWelcomeMessage: false,
      allowNotifications: false,
      fetchForNotification: false,
      notificationPoolingFrequency: 15,
      // File upload configuration (chat input attachments)
      allowFileUpload: false,
      allowImagePaste: false,
      allowedFileTypes: '',
      maxFileSize: 10,
      maxFiles: 5,
      feedbackEnabled: false,
      feedbackPositiveUrl: '',
      feedbackNegativeUrl: '',
      feedbackMinChars: 20,
      recaptchaEnabled: false,
      recaptchaSiteKey: '',
      recaptchaVersion: 'v2' as 'v2' | 'v3',
      footnoteEnabled: false,
      agentMessageFootnote: '',
      footnoteTitle: '',
      footnoteCollapsible: true,
      footnoteShowTiming: 'after_response',
      // Light theme
      themePrimary: '',
      themeSecondary: '',
      themeBackground: '',
      themeForeground: '',
      themeCard: '',
      themeCardForeground: '',
      themePrimaryForeground: '',
      themeSecondaryForeground: '',
      themeMuted: '',
      themeMutedForeground: '',
      themeBorder: '',
      themeInput: '',
      // Dark theme
      themeDarkPrimary: '',
      themeDarkSecondary: '',
      themeDarkBackground: '',
      themeDarkForeground: '',
      themeDarkCard: '',
      themeDarkCardForeground: '',
      themeDarkPrimaryForeground: '',
      themeDarkSecondaryForeground: '',
      themeDarkMuted: '',
      themeDarkMutedForeground: '',
      themeDarkBorder: '',
      themeDarkInput: '',
      isActive: true,
      isPublic: false,
      translations: initialTranslations
    });

    // Set default language
    const defaultLang = languages.find(l => l.isDefault) || languages[0];
    setSelectedAgentLanguage(defaultLang?.id || '');

    setShowAgentForm(true);
  };

  // Embedded content CRUD functions
  const handleCreateEmbeddedContent = () => {
    setEditingEmbeddedContent(null);
    setEmbeddedContentForm({
      name: '',
      description: '',
      icon: 'Bot',
      color: '#3b82f6',
      embeddingUrl: '',
      title: '',
      isActive: true,
    });
    setShowEmbeddedContentForm(true);
  };

  const handleEditAgent = (agent: any) => {
    setEditingAgent(agent);

    // Initialize translations for all languages
    const agentTranslations: Record<string, any> = {};
    languages.forEach(lang => {
      // Get existing translation or create empty one
      const existingTranslation = agent.translations?.find((t: any) => t.languageId === lang.id);
      agentTranslations[lang.id] = {
        name: existingTranslation?.name || '',
        description: existingTranslation?.description || '',
        chatTitle: existingTranslation?.chatTitle || '',
        welcomeMessage: existingTranslation?.welcomeMessage || ''
      };
    });

    setAgentForm({
      name: agent.name || '',
      icon: agent.icon || 'Bot',
      color: agent.color || '#3b82f6',
      chatApiUrl: agent.chatApiUrl || '',
      agentSid: agent.agentSid || '',
      agentSidSource: (agent.agentSidSource || 'user_email') as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom',
      chatUserId: agent.chatUserId || '',
      customFavicon: agent.customFavicon || '',
      streamingEnabled: agent.streamingEnabled ?? false,
      enablePersistence: agent.enablePersistence ?? false,
      enablePreemptiveEndChatButton: agent.enablePreemptiveEndChatButton ?? false,
      enableClearChatButton: agent.enableClearChatButton ?? false,
      showRouteStack: agent.showRouteStack ?? false,
      fetchForWelcomeMessage: agent.fetchForWelcomeMessage ?? false,
      allowNotifications: agent.allowNotifications ?? false,
      fetchForNotification: agent.fetchForNotification ?? false,
      notificationPoolingFrequency: agent.notificationPoolingFrequency ?? 15,
      // File upload configuration (chat input attachments)
      allowFileUpload: agent.allowFileUpload ?? false,
      allowImagePaste: agent.allowImagePaste ?? false,
      allowedFileTypes: agent.allowedFileTypes || '',
      maxFileSize: agent.maxFileSize ?? 10,
      maxFiles: agent.maxFiles ?? 5,
      feedbackEnabled: agent.feedbackEnabled ?? false,
      feedbackPositiveUrl: agent.feedbackPositiveUrl || '',
      feedbackNegativeUrl: agent.feedbackNegativeUrl || '',
      feedbackMinChars: agent.feedbackMinChars ?? 20,
      recaptchaEnabled: agent.recaptchaEnabled ?? false,
      recaptchaSiteKey: agent.recaptchaSiteKey || '',
      recaptchaVersion: agent.recaptchaVersion || 'v2',
      footnoteEnabled: agent.footnoteEnabled ?? false,
      agentMessageFootnote: agent.agentMessageFootnote || '',
      footnoteTitle: agent.footnoteTitle || '',
      footnoteCollapsible: agent.footnoteCollapsible ?? true,
      footnoteShowTiming: agent.footnoteShowTiming || 'after_response',
      // Light theme
      themePrimary: agent.themePrimary || '',
      themeSecondary: agent.themeSecondary || '',
      themeBackground: agent.themeBackground || '',
      themeForeground: agent.themeForeground || '',
      themeCard: agent.themeCard || '',
      themeCardForeground: agent.themeCardForeground || '',
      themePrimaryForeground: agent.themePrimaryForeground || '',
      themeSecondaryForeground: agent.themeSecondaryForeground || '',
      themeMuted: agent.themeMuted || '',
      themeMutedForeground: agent.themeMutedForeground || '',
      themeBorder: agent.themeBorder || '',
      themeInput: agent.themeInput || '',
      // Dark theme
      themeDarkPrimary: agent.themeDarkPrimary || '',
      themeDarkSecondary: agent.themeDarkSecondary || '',
      themeDarkBackground: agent.themeDarkBackground || '',
      themeDarkForeground: agent.themeDarkForeground || '',
      themeDarkCard: agent.themeDarkCard || '',
      themeDarkCardForeground: agent.themeDarkCardForeground || '',
      themeDarkPrimaryForeground: agent.themeDarkPrimaryForeground || '',
      themeDarkSecondaryForeground: agent.themeDarkSecondaryForeground || '',
      themeDarkMuted: agent.themeDarkMuted || '',
      themeDarkMutedForeground: agent.themeDarkMutedForeground || '',
      themeDarkBorder: agent.themeDarkBorder || '',
      themeDarkInput: agent.themeDarkInput || '',
      isActive: agent.isActive ?? true,
      isPublic: agent.isPublic ?? false,
      translations: agentTranslations
    });

    // Set default language
    const defaultLang = languages.find(l => l.isDefault) || languages[0];
    setSelectedAgentLanguage(defaultLang?.id || '');

    setShowAgentForm(true);
  };

  const handleEditEmbeddedContent = (embeddedContent: any) => {
    setEditingEmbeddedContent(embeddedContent);
    setEmbeddedContentForm({
      name: embeddedContent.name || '',
      description: embeddedContent.description || '',
      icon: embeddedContent.icon || 'Bot',
      color: embeddedContent.color || '#3b82f6',
      embeddingUrl: embeddedContent.embeddingUrl || '',
      title: embeddedContent.title || '',
      isActive: embeddedContent.isActive ?? true,
    });
    setShowEmbeddedContentForm(true);
  };

  const handleDeleteAgent = async (agentId: string) => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !confirm('Are you sure you want to delete this agent?')) return;

    try {
      await apiService.deleteAgent(user.tenantId, agentId);
      await loadAgents();
      notifySuccess('Deleted', 'Agent deleted successfully');
    } catch (err) {
      console.error('Failed to delete agent:', err);
      notifyError('Failed to delete agent');
    }
  };

  const handleDeleteEmbeddedContent = async (embeddedContentId: string) => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !confirm('Are you sure you want to delete this embedded content?')) return;

    try {
      await apiService.deleteEmbeddedContent(user.tenantId, embeddedContentId);
      await loadEmbeddedContents();
      notifySuccess('Deleted', 'Embedded content deleted successfully');
    } catch (err) {
      console.error('Failed to delete embedded content:', err);
      notifyError('Failed to delete embedded content');
    }
  };

  const handleSaveAgent = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      // Convert translations object to array format for API
      const translationsArray = Object.entries(agentForm.translations).map(([languageId, trans]) => ({
        languageId,
        name: trans.name,
        description: trans.description,
        chatTitle: trans.chatTitle,
        welcomeMessage: trans.welcomeMessage
      }));

      const agentData = {
        ...agentForm,
        translations: translationsArray
      };

      if (editingAgent) {
        await apiService.updateAgent(user.tenantId, editingAgent.id, agentData);
      } else {
        await apiService.createAgent(user.tenantId, agentData);
      }

      setShowAgentForm(false);
      await loadAgents();
      notifySuccess(editingAgent ? 'Updated' : 'Created', editingAgent ? 'Agent updated successfully' : 'Agent created successfully');
    } catch (err) {
      console.error('Failed to save agent:', err);
      notifyError('Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmbeddedContent = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      if (editingEmbeddedContent) {
        await apiService.updateEmbeddedContent(user.tenantId, editingEmbeddedContent.id, embeddedContentForm);
      } else {
        await apiService.createEmbeddedContent(user.tenantId, embeddedContentForm);
      }

      setShowEmbeddedContentForm(false);
      await loadEmbeddedContents();
      notifySuccess(editingEmbeddedContent ? 'Updated' : 'Created', editingEmbeddedContent ? 'Embedded content updated successfully' : 'Embedded content created successfully');
    } catch (err) {
      console.error('Failed to save embedded content:', err);
      notifyError('Failed to save embedded content');
    } finally {
      setSaving(false);
    }
  };

  const getIconComponent = (iconName: string) => {
    const iconOption = iconOptions.find(option => option.value === iconName);
    return iconOption ? iconOption.icon : Bot;
  };

  // Home screen functions
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id.toString());
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (!over) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    // Handle drag from toolbar to canvas
    if (activeId.startsWith('toolbar-')) {
      // Check if dropping into a grouping container
      const targetGroup = homeScreenItems.find(item =>
        overId === `group-dropzone-${item.id}` && item.type === 'grouping-container'
      );

      // Check if dropping onto canvas or onto an existing item in canvas
      const isDroppedOnCanvas = overId === 'home-screen-canvas' ||
        overId === 'canvas-dropzone' ||
        homeScreenItems.some(item => item.id === overId) ||
        targetGroup;

      if (isDroppedOnCanvas) {
        const itemType = activeId.replace('toolbar-', '');
        let newItem;

        if (itemType === 'grouping-container') {
          newItem = {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for local state
            type: 'grouping-container',
            name: 'New Group',
            items: [],
            parentId: null,
            order: homeScreenItems.filter(i => !i.parentId).length,
            isActive: true,
          };
        } else if (itemType.startsWith('agent-')) {
          const agentId = itemType.replace('agent-', '');
          newItem = {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for local state
            type: 'agent',
            agentId,
            parentId: targetGroup ? targetGroup.id : null,
            order: targetGroup
              ? homeScreenItems.filter(i => i.parentId === targetGroup.id).length
              : homeScreenItems.filter(i => !i.parentId).length,
            isActive: true,
          };
        } else if (itemType.startsWith('embedded-')) {
          const embeddedContentId = itemType.replace('embedded-', '');
          newItem = {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for local state
            type: 'embedded',
            embeddedContentId,
            parentId: targetGroup ? targetGroup.id : null,
            order: targetGroup
              ? homeScreenItems.filter(i => i.parentId === targetGroup.id).length
              : homeScreenItems.filter(i => !i.parentId).length,
            isActive: true,
          };
        }

        if (newItem) {
          setHomeScreenItems(prev => [...prev, newItem]);
        }
      }
    } else {
      // Handle moving existing items
      const draggedItem = homeScreenItems.find(item => item.id === activeId);

      if (draggedItem) {
        // Check if dropping into a group dropzone
        const targetGroupMatch = overId.match(/^group-dropzone-(.+)$/);

        // Check if dropping onto main canvas (set parent to null)
        const isMainCanvas = overId === 'home-screen-canvas' || overId === 'canvas-dropzone';

        // Check if dropping onto another item (could be for reordering or parent change)
        const targetItem = homeScreenItems.find(item => item.id === overId);

        if (targetGroupMatch) {
          // Dropping into a specific group
          const targetParentId = targetGroupMatch[1];

          if (draggedItem.parentId !== targetParentId && draggedItem.type !== 'grouping-container') {
            // Update the item's parent locally (don't allow groups inside groups)
            setHomeScreenItems(prev => prev.map(item =>
              item.id === draggedItem.id
                ? { ...item, parentId: targetParentId }
                : item
            ));
          }
        } else if (isMainCanvas) {
          // Dropping onto main canvas - remove parent
          if (draggedItem.parentId !== null) {
            setHomeScreenItems(prev => prev.map(item =>
              item.id === draggedItem.id
                ? { ...item, parentId: null }
                : item
            ));
          }
        } else if (targetItem && !targetItem.parentId && draggedItem.parentId) {
          // Dropping from a group onto a root-level item (move to root)
          setHomeScreenItems(prev => prev.map(item =>
            item.id === draggedItem.id
              ? { ...item, parentId: null }
              : item
          ));
        }
      }
    }
  };

  const handleReorderItem = (e: React.MouseEvent, itemId: string, direction: 'left' | 'right') => {
    e.preventDefault();
    e.stopPropagation();

    const item = homeScreenItems.find(i => i.id === itemId);
    if (!item) return;

    // Get siblings (items with same parentId)
    const siblings = homeScreenItems
      .filter(i => i.parentId === item.parentId)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const currentIndex = siblings.findIndex(i => i.id === itemId);
    if (currentIndex === -1) return;

    // Check boundaries
    if (direction === 'left' && currentIndex === 0) return;
    if (direction === 'right' && currentIndex === siblings.length - 1) return;

    // Swap with neighbor
    const targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1;
    const currentItem = siblings[currentIndex];
    const targetItem = siblings[targetIndex];

    // Swap order values
    setHomeScreenItems(prev => prev.map(item => {
      if (item.id === currentItem.id) {
        return { ...item, order: targetItem.order };
      }
      if (item.id === targetItem.id) {
        return { ...item, order: currentItem.order };
      }
      return item;
    }));
  };

  const handleDeleteHomeScreenItem = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if this is a group with children
    const itemToDelete = homeScreenItems.find(item => item.id === itemId);
    const hasChildren = itemToDelete?.type === 'grouping-container' &&
      homeScreenItems.some(item => item.parentId === itemId);

    const confirmMessage = hasChildren
      ? 'This group contains items. Are you sure you want to remove the group and all its items?'
      : 'Are you sure you want to remove this item?';

    if (!confirm(confirmMessage)) return;

    // Remove item and all its children from local state
    setHomeScreenItems(prev => prev.filter(item =>
      item.id !== itemId && item.parentId !== itemId
    ));
  };

  const handleSaveHomeScreen = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      // Send the entire home screen configuration to replace the existing one
      await apiService.replaceHomeScreen(user.tenantId, homeScreenItems);

      // Reload from server to get actual IDs for any temp items
      await loadHomeScreenItems();

      notifySuccess('Saved', 'Home screen configuration saved');
    } catch (err) {
      console.error('Failed to save home screen:', err);
      notifyError('Failed to save home screen configuration');
    } finally {
      setSaving(false);
    }
  };

  // i18n management functions
  const loadLanguages = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      const tenantLanguages = await apiService.getTenantLanguages(user.tenantId);
      setLanguages(tenantLanguages);
      if (tenantLanguages.length > 0 && !selectedLanguage) {
        setSelectedLanguage(tenantLanguages[0].id);
        await loadTranslations(tenantLanguages[0].id);
      }
    } catch (err) {
      console.error('Failed to load languages:', err);
      notifyError('Failed to load languages');
    }
  };

  const loadTranslations = async (languageId: string) => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      const languageTranslations = await apiService.getLanguageTranslations(user.tenantId, languageId);
      setTranslationList(languageTranslations);
    } catch (err) {
      console.error('Failed to load translations:', err);
      notifyError('Failed to load translations');
    }
  };

  const handleCreateLanguage = () => {
    setLanguageForm({
      code: '',
      name: '',
      nativeName: '',
      isActive: true,
      isDefault: false,
    });
    setShowLanguageForm(true);
  };

  const handleSaveLanguage = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);
      await apiService.createLanguage(user.tenantId, languageForm);
      setShowLanguageForm(false);
      await loadLanguages();
      notifySuccess('Created', 'Language created successfully');
    } catch (err) {
      console.error('Failed to save language:', err);
      notifyError('Failed to save language');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTranslation = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !selectedLanguage) return;

    try {
      setSaving(true);
      await apiService.upsertTranslation(user.tenantId, selectedLanguage, translationForm);
      await loadTranslations(selectedLanguage);
      setTranslationForm({ key: '', value: '', context: '' });
      notifySuccess('Saved', 'Translation saved');
    } catch (err) {
      console.error('Failed to save translation:', err);
      notifyError('Failed to save translation');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setConfig(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // User management functions
  const loadUsers = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setUsersLoading(true);
      const tenantUsers = await apiService.getTenantUsers(user.tenantId);
      setUsers(tenantUsers);
    } catch (err) {
      console.error('Failed to load users:', err);
      notifyError('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUserGroups = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setUserGroupsLoading(true);
      const tenantUserGroups = await apiService.getTenantUserGroups(user.tenantId);
      setUserGroups(tenantUserGroups);
    } catch (err) {
      console.error('Failed to load user groups:', err);
      notifyError('Failed to load user groups');
    } finally {
      setUserGroupsLoading(false);
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      name: '',
      email: '',
      password: '',
      role: 'user',
      isActive: true,
    });
    setShowUserForm(true);
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't populate password for security
      role: user.role || 'user',
      isActive: user.isActive ?? true,
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId: string) => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !confirm('Are you sure you want to delete this user?')) return;

    try {
      await apiService.deleteUser(user.tenantId, userId);
      await loadUsers();
      notifySuccess('Deleted', 'User deleted successfully');
    } catch (err) {
      console.error('Failed to delete user:', err);
      notifyError('Failed to delete user');
    }
  };

  const handleSaveUser = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      if (editingUser) {
        await apiService.updateUser(user.tenantId, editingUser.id, userForm);
      } else {
        await apiService.createUser(user.tenantId, userForm);
      }

      setShowUserForm(false);
      await loadUsers();
      notifySuccess(editingUser ? 'Updated' : 'Created', editingUser ? 'User updated successfully' : 'User created successfully');
    } catch (err) {
      console.error('Failed to save user:', err);
      notifyError('Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const user = apiService.getStoredUser();
    if (!user?.id) return;

    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      notifyError('New passwords do not match', 'Validation error');
      return;
    }

    try {
      setSaving(true);

      await apiService.changePassword(user.id, changePasswordForm);

      setShowChangePasswordForm(false);
      setChangePasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      notifySuccess('Updated', 'Password changed successfully');
    } catch (err) {
      console.error('Failed to change password:', err);
      notifyError('Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAuthConfig = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    // Validation: at least one login method must be enabled
    if (!openidConfig.enabled && !localLoginEnabled) {
      notifyError('At least one login method (OpenID Connect or Local Login) must be enabled.', 'Validation error');
      return;
    }

    try {
      setSaving(true);

      // Save OpenID config
      await apiService.updateTenantOpenIDConfig(user.tenantId, openidConfig);

      // Save local login setting via the general config endpoint
      await apiService.updateTenantConfig(user.tenantId, {
        localLoginEnabled: localLoginEnabled
      });

      notifySuccess('Saved', 'Authentication configuration saved');
    } catch (err) {
      console.error('Failed to save authentication configuration:', err);
      notifyError('Failed to save authentication configuration');
    } finally {
      setSaving(false);
    }
  };

  // User Groups Management functions
  const handleCreateUserGroup = () => {
    setEditingUserGroup(null);
    setUserGroupForm({
      name: '',
      description: '',
      userIds: [],
    });
    setShowUserGroupForm(true);
  };

  const handleEditUserGroup = (group: any) => {
    setEditingUserGroup(group);
    setUserGroupForm({
      name: group.name || '',
      description: group.description || '',
      userIds: group.users?.map((u: any) => u.id) || [],
    });
    setShowUserGroupForm(true);
  };

  const handleDeleteUserGroup = async (groupId: string) => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !confirm('Are you sure you want to delete this user group?')) return;

    try {
      await apiService.deleteUserGroup(user.tenantId, groupId);
      await loadUserGroups();
      notifySuccess('Deleted', 'User group deleted successfully');
    } catch (err) {
      console.error('Failed to delete user group:', err);
      notifyError('Failed to delete user group');
    }
  };

  const handleSaveUserGroup = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      setSaving(true);

      if (editingUserGroup) {
        await apiService.updateUserGroup(user.tenantId, editingUserGroup.id, userGroupForm);
      } else {
        await apiService.createUserGroup(user.tenantId, userGroupForm);
      }

      setShowUserGroupForm(false);
      await loadUserGroups();
      notifySuccess(editingUserGroup ? 'Updated' : 'Created', editingUserGroup ? 'User group updated successfully' : 'User group created successfully');
    } catch (err) {
      console.error('Failed to save user group:', err);
      notifyError('Failed to save user group');
    } finally {
      setSaving(false);
    }
  };

  // Home Screen Group Editing functions
  const handleEditHomeScreenGroup = (group: any) => {
    setEditingHomeScreenGroup(group);
    setGroupEditForm({
      name: group.name || '',
      showTitle: group.showTitle ?? true,
      groupAccess: normalizeGroupAccess(group.groupAccess),
    });
    setShowGroupEditForm(true);
  };

  const handleSaveHomeScreenGroup = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !editingHomeScreenGroup) return;

    try {
      setSaving(true);

      // Update the item in the server if it has a real ID
      if (!editingHomeScreenGroup.id.startsWith('temp-')) {
        await apiService.updateHomeScreenItem(user.tenantId, editingHomeScreenGroup.id, groupEditForm);
      }

      // Update local state
      setHomeScreenItems(prev => prev.map(item =>
        item.id === editingHomeScreenGroup.id
          ? { ...item, ...groupEditForm }
          : item
      ));

      setShowGroupEditForm(false);
      notifySuccess('Saved', 'Group settings saved');
    } catch (err) {
      console.error('Failed to save group settings:', err);
      notifyError('Failed to save group settings');
    } finally {
      setSaving(false);
    }
  };

  // Load users, groups and auth config when user management tab is selected
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadUserGroups();
      loadAuthConfig();
    }
    // Home screen group access needs user groups too
    if (activeTab === 'home') {
      loadUserGroups();
    }
  }, [activeTab]);

  // Ensure user groups are loaded when opening the group edit modal (e.g. if user never visited the Users tab)
  useEffect(() => {
    if (showGroupEditForm && userGroups.length === 0 && !userGroupsLoading) {
      loadUserGroups();
    }
  }, [showGroupEditForm]);

  const loadAuthConfig = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId) return;

    try {
      // Load OpenID config
      const response = await apiService.getTenantOpenIDConfig(user.tenantId);
      if (response.config) {
        setOpenidConfig({
          enabled: response.config.enabled || false,
          providerName: response.config.providerName || '',
          logoUrl: response.config.logoUrl || '',
          clientId: response.config.clientId || '',
          clientSecret: response.config.clientSecret || '',
          authority: response.config.authority || '',
          authorizeUrl: response.config.authorizeUrl || '',
          tokenUrl: response.config.tokenUrl || '',
          profileUrl: response.config.profileUrl || '',
          scope: response.config.scope || 'openid profile email',
          jsonPathEmail: response.config.jsonPathEmail || 'email',
          jsonPathName: response.config.jsonPathName || 'name',
          jsonPathOidcUserId: response.config.jsonPathOidcUserId || 'sub',
          redirectUri: response.config.redirectUri || '',
          buttonTemplate: response.buttonConfig?.template || '',
          buttonClasses: response.buttonConfig?.classes || '',
          buttonImageUrl: response.buttonConfig?.imageUrl || '',
        });
      }
      if (response.callbackUrl) {
        setCallbackUrl(response.callbackUrl);
      }
    } catch (err) {
      console.error('Failed to load auth config:', err);
      notifyError('Failed to load authentication configuration');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background p-6 min-h-screen">
      <div className="w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Settings className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Setup Dashboard</h1>
          <p className="text-muted-foreground">
            Configure your white-label application settings
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-card border border-border rounded-lg shadow-sm">
          <div className="border-b border-border">
            <nav className="flex">
              <button
                onClick={() => handleTabChange('application')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'application'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Settings className="w-4 h-4 mx-auto mb-2" />
                Application Settings
              </button>
              <button
                onClick={() => handleTabChange('agents')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'agents'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Bot className="w-4 h-4 mx-auto mb-2" />
                Agent Management
              </button>
              <button
                onClick={() => handleTabChange('embedded')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'embedded'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Layout className="w-4 h-4 mx-auto mb-2" />
                Embedded Content
              </button>
              <button
                onClick={() => handleTabChange('home')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'home'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Info className="w-4 h-4 mx-auto mb-2" />
                Home Screen
              </button>
              <button
                onClick={() => handleTabChange('users')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'users'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <Users className="w-4 h-4 mx-auto mb-2" />
                User Management
              </button>
              <button
                onClick={() => handleTabChange('i18n')}
                className={`flex-1 px-6 py-4 text-center border-b-2 font-medium text-sm transition-colors ${activeTab === 'i18n'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <HelpCircle className="w-4 h-4 mx-auto mb-2" />
                Internationalization
              </button>
            </nav>
          </div>

          {/* Application Settings Tab */}
          {activeTab === 'application' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-card-foreground mb-2">Application Settings</h2>
                <p className="text-muted-foreground">
                  Customize the appearance and messaging of your application
                </p>
              </div>

              <div className="space-y-6">
                {/* Page Title */}
                <div className="space-y-2">
                  <label htmlFor="pageTitle" className="text-sm font-medium text-foreground">
                    Page Title
                  </label>
                  <input
                    id="pageTitle"
                    type="text"
                    value={config.pageTitle}
                    onChange={(e) => handleInputChange('pageTitle', e.target.value)}
                    placeholder="app.title (for translations) or literal text"
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent font-mono text-sm"
                  />
                  <p className="text-sm text-muted-foreground">
                    Use translation keys like 'app.title' or enter literal text. This title will appear in the browser tab and as the main heading.
                  </p>
                </div>

                {/* Welcome Message */}
                <div className="space-y-2">
                  <label htmlFor="welcomeMessage" className="text-sm font-medium text-foreground">
                    Welcome Message
                  </label>
                  <textarea
                    id="welcomeMessage"
                    value={config.welcomeMessage}
                    onChange={(e) => handleInputChange('welcomeMessage', e.target.value)}
                    placeholder="app.welcomeMessage (for translations) or literal text"
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent min-h-[100px] resize-vertical font-mono text-sm"
                    rows={4}
                  />
                  <p className="text-sm text-muted-foreground">
                    Use translation keys like 'app.welcomeMessage' or enter literal text. This message will be shown when users first open a chat.
                  </p>
                </div>

                {/* Logo Configuration */}
                <div className="border-t border-border pt-6 space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Logo Configuration</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Configure how your logo appears in the top bar. You can use text only, a logo image, or both (logo followed by text).
                    </p>
                  </div>

                  {/* Company Name */}
                  <div className="space-y-2">
                    <label htmlFor="companyName" className="text-sm font-medium text-foreground">
                      Company Name
                    </label>
                    <input
                      id="companyName"
                      type="text"
                      value={config.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="Your Company Name"
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    />
                    <p className="text-sm text-muted-foreground">
                      Company name used for text logo display
                    </p>
                  </div>

                  {/* Use Text Logo Option */}
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="useTextLogo"
                      checked={config.useTextLogo}
                      onChange={(e) => handleInputChange('useTextLogo', e.target.checked)}
                      className="rounded border-input"
                    />
                    <label htmlFor="useTextLogo" className="text-sm font-medium text-foreground">
                      Show text logo (company name)
                    </label>
                  </div>
                  <p className="text-sm text-muted-foreground ml-6">
                    When enabled, the company name will be displayed as text. You can also show both logo and text.
                  </p>

                  {/* Logo Path (URL) */}
                  <div className="space-y-2">
                    <label htmlFor="logoPath" className="text-sm font-medium text-foreground">
                      Logo URL
                    </label>
                    <input
                      id="logoPath"
                      type="url"
                      value={config.logoPath}
                      onChange={(e) => handleInputChange('logoPath', e.target.value)}
                      placeholder="https://example.com/logo.png or /path/to/logo.png"
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                    />
                    <p className="text-sm text-muted-foreground">
                      URL or path to your logo image. Can be a full URL or a relative path (e.g., /logo.png). Leave empty if you only want text.
                    </p>
                  </div>

                  {/* Logo Base64 */}
                  <div className="space-y-2">
                    <label htmlFor="logoBase64" className="text-sm font-medium text-foreground">
                      Logo Base64 (Alternative)
                    </label>
                    <textarea
                      id="logoBase64"
                      value={config.logoBase64}
                      onChange={(e) => handleInputChange('logoBase64', e.target.value)}
                      placeholder="data:image/png;base64,iVBORw0KGgo..."
                      className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent min-h-[100px] resize-vertical font-mono text-sm"
                      rows={4}
                    />
                    <p className="text-sm text-muted-foreground">
                      Alternatively, you can provide a base64-encoded image. This takes precedence over Logo URL if both are provided.
                    </p>
                  </div>

                  {/* Logo Preview */}
                  {(config.logoBase64 || config.logoPath || (config.useTextLogo && config.companyName)) && (
                    <div className="space-y-3 pt-4 border-t border-border">
                      <label className="text-sm font-medium text-foreground">Preview</label>
                      
                      {/* Light Mode Preview */}
                      <div className="space-y-2">
                        <span className="text-xs text-muted-foreground font-medium">Light Mode</span>
                        <div className="bg-white p-4 rounded-lg border border-border flex items-center space-x-3">
                          {config.logoBase64 && (
                            <img
                              src={config.logoBase64}
                              alt="Logo Preview (Light)"
                              className="h-8 w-auto object-contain"
                              style={{
                                filter: `brightness(${config.logoLightBrightness}) contrast(${config.logoLightContrast}) drop-shadow(0 1px 2px rgba(0, 0, 0, ${config.logoLightDropShadowOpacity}))`,
                              }}
                            />
                          )}
                          {!config.logoBase64 && config.logoPath && (
                            <img
                              src={config.logoPath}
                              alt="Logo Preview (Light)"
                              className="h-8 w-auto object-contain"
                              style={{
                                filter: `brightness(${config.logoLightBrightness}) contrast(${config.logoLightContrast}) drop-shadow(0 1px 2px rgba(0, 0, 0, ${config.logoLightDropShadowOpacity}))`,
                              }}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          {config.useTextLogo && config.companyName && (
                            <span className="text-xl font-bold text-gray-900">
                              {config.companyName}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Dark Mode Preview */}
                      <div className="space-y-2">
                        <span className="text-xs text-muted-foreground font-medium">Dark Mode</span>
                        <div className="bg-gray-900 p-4 rounded-lg border border-gray-700 flex items-center space-x-3">
                          {config.logoBase64 && (
                            <div className="relative inline-flex items-center">
                              <div className="absolute inset-0 bg-gray-900/30 rounded-sm blur-sm -z-10" />
                              <img
                                src={config.logoBase64}
                                alt="Logo Preview (Dark)"
                                className="h-8 w-auto object-contain"
                                style={{
                                  filter: `brightness(${config.logoDarkBrightness}) contrast(${config.logoDarkContrast}) drop-shadow(0 1px 2px rgba(255, 255, 255, ${config.logoDarkDropShadowOpacity}))`,
                                }}
                              />
                            </div>
                          )}
                          {!config.logoBase64 && config.logoPath && (
                            <div className="relative inline-flex items-center">
                              <div className="absolute inset-0 bg-gray-900/30 rounded-sm blur-sm -z-10" />
                              <img
                                src={config.logoPath}
                                alt="Logo Preview (Dark)"
                                className="h-8 w-auto object-contain"
                                style={{
                                  filter: `brightness(${config.logoDarkBrightness}) contrast(${config.logoDarkContrast}) drop-shadow(0 1px 2px rgba(255, 255, 255, ${config.logoDarkDropShadowOpacity}))`,
                                }}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          {config.useTextLogo && config.companyName && (
                            <span className="text-xl font-bold text-gray-100">
                              {config.companyName}
                            </span>
                          )}
                        </div>
                      </div>

                    </div>
                  )}

                  {/* Logo Enhancement Settings */}
                  <div className="space-y-6 pt-6 border-t border-border">
                    <div>
                      <h4 className="text-base font-medium text-foreground mb-2">Logo Enhancement Settings</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Adjust brightness, contrast, and drop shadow for better logo visibility in different themes.
                      </p>
                    </div>

                    {/* Light Mode Settings */}
                    <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Sun className="w-4 h-4" />
                        Light Mode Settings
                      </h5>
                      
                      <div className="space-y-4">
                        {/* Brightness */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Brightness</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoLightBrightness).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.05"
                            value={config.logoLightBrightness}
                            onChange={(e) => handleInputChange('logoLightBrightness', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0.5x</span>
                            <span>1.0x (normal)</span>
                            <span>2.0x</span>
                          </div>
                        </div>

                        {/* Contrast */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Contrast</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoLightContrast).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.05"
                            value={config.logoLightContrast}
                            onChange={(e) => handleInputChange('logoLightContrast', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0.5x</span>
                            <span>1.0x (normal)</span>
                            <span>2.0x</span>
                          </div>
                        </div>

                        {/* Drop Shadow Opacity */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Drop Shadow Opacity</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoLightDropShadowOpacity).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={config.logoLightDropShadowOpacity}
                            onChange={(e) => handleInputChange('logoLightDropShadowOpacity', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0 (off)</span>
                            <span>0.5</span>
                            <span>1.0 (max)</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dark Mode Settings */}
                    <div className="space-y-4 p-4 bg-gray-900/50 dark:bg-gray-800/50 border border-gray-700 dark:border-gray-600 rounded-lg">
                      <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Moon className="w-4 h-4" />
                        Dark Mode Settings
                      </h5>
                      
                      <div className="space-y-4">
                        {/* Brightness */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Brightness</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoDarkBrightness).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.05"
                            value={config.logoDarkBrightness}
                            onChange={(e) => handleInputChange('logoDarkBrightness', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0.5x</span>
                            <span>1.0x (normal)</span>
                            <span>2.0x</span>
                          </div>
                        </div>

                        {/* Contrast */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Contrast</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoDarkContrast).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0.5"
                            max="2.0"
                            step="0.05"
                            value={config.logoDarkContrast}
                            onChange={(e) => handleInputChange('logoDarkContrast', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0.5x</span>
                            <span>1.0x (normal)</span>
                            <span>2.0x</span>
                          </div>
                        </div>

                        {/* Drop Shadow Opacity */}
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <label className="text-sm font-medium text-foreground">Drop Shadow Opacity</label>
                            <span className="text-sm text-muted-foreground font-mono">{parseFloat(config.logoDarkDropShadowOpacity).toFixed(2)}</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={config.logoDarkDropShadowOpacity}
                            onChange={(e) => handleInputChange('logoDarkDropShadowOpacity', e.target.value)}
                            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>0 (off)</span>
                            <span>0.5</span>
                            <span>1.0 (max)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Icon Configuration */}
                <div className="border-t border-border pt-6 space-y-6">
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Icon Configuration</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Customize the icons used throughout the application. Select icons for different UI elements and actions.
                    </p>
                  </div>

                  {/* Navigation Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Navigation Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Home"
                          selectedIcon={iconConfig.home}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, home: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Settings"
                          selectedIcon={iconConfig.settings}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, settings: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="User"
                          selectedIcon={iconConfig.user}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, user: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Users"
                          selectedIcon={iconConfig.users}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, users: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Logout"
                          selectedIcon={iconConfig.logout}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, logout: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Action Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Save"
                          selectedIcon={iconConfig.save}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, save: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Edit"
                          selectedIcon={iconConfig.edit}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, edit: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Delete"
                          selectedIcon={iconConfig.delete}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, delete: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Add"
                          selectedIcon={iconConfig.add}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, add: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Cancel"
                          selectedIcon={iconConfig.cancel}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, cancel: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Refresh"
                          selectedIcon={iconConfig.refresh}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, refresh: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Status Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Status Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Loading"
                          selectedIcon={iconConfig.loading}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, loading: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Success"
                          selectedIcon={iconConfig.success}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, success: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Error"
                          selectedIcon={iconConfig.error}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, error: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Info"
                          selectedIcon={iconConfig.info}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, info: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Help"
                          selectedIcon={iconConfig.help}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, help: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* UI Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">UI Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Theme Toggle (Moon/Sun)"
                          selectedIcon={iconConfig.themeToggle}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, themeToggle: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Language"
                          selectedIcon={iconConfig.language}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, language: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Chevron Down"
                          selectedIcon={iconConfig.chevronDown}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, chevronDown: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Chevron Left"
                          selectedIcon={iconConfig.chevronLeft}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, chevronLeft: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Chevron Right"
                          selectedIcon={iconConfig.chevronRight}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, chevronRight: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Arrow Left"
                          selectedIcon={iconConfig.arrowLeft}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, arrowLeft: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Arrow Right"
                          selectedIcon={iconConfig.arrowRight}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, arrowRight: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="External Link"
                          selectedIcon={iconConfig.externalLink}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, externalLink: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Agent Type Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Agent Type Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Bot"
                          selectedIcon={iconConfig.bot}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, bot: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Message Circle"
                          selectedIcon={iconConfig.messageCircle}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, messageCircle: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Message Square"
                          selectedIcon={iconConfig.messageSquare}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, messageSquare: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Shopping Cart"
                          selectedIcon={iconConfig.shoppingCart}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, shoppingCart: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Phone"
                          selectedIcon={iconConfig.phone}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, phone: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Mail"
                          selectedIcon={iconConfig.mail}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, mail: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Setup Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Setup Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Key"
                          selectedIcon={iconConfig.key}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, key: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Shield"
                          selectedIcon={iconConfig.shield}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, shield: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Layout"
                          selectedIcon={iconConfig.layout}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, layout: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Folder"
                          selectedIcon={iconConfig.folder}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, folder: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Chat Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Chat Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Maximize"
                          selectedIcon={iconConfig.maximize}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, maximize: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Minimize"
                          selectedIcon={iconConfig.minimize}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, minimize: icon }))}
                        />
                      </div>
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Phone Off"
                          selectedIcon={iconConfig.phoneOff}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, phoneOff: icon }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional Icons */}
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <h4 className="font-medium text-foreground mb-3">Additional Icons</h4>
                    <div className="flex flex-wrap gap-4">
                      <div className="min-w-[200px] flex-1 max-w-[60px]">
                        <IconPicker
                          label="Check"
                          selectedIcon={iconConfig.check}
                          onIconSelect={(icon) => setIconConfig(prev => ({ ...prev, check: icon }))}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] justify-center"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
              {/* Preview Section */}
              <div className="bg-card border border-border rounded-lg shadow-sm">
                <div className="p-6 border-b border-border">
                  <h2 className="text-xl font-semibold text-card-foreground mb-2">Preview</h2>
                  <p className="text-muted-foreground">
                    See how your settings will appear to users
                  </p>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2 text-foreground">Browser Title:</h3>
                      <div className="bg-muted p-3 rounded border font-mono text-sm text-muted-foreground">
                        {resolveTranslationKey(config.pageTitle, translations, 'Multi-Agent Chat Interface')}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {config.pageTitle ? (isTranslationKey(config.pageTitle) ? 'Using translation key' : 'Using literal text') : 'Default value'}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2 text-foreground">Welcome Message:</h3>
                      <div className="bg-muted p-3 rounded border text-sm text-muted-foreground">
                        {resolveTranslationKey(config.welcomeMessage, translations, 'No welcome message configured')}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {config.welcomeMessage ? (isTranslationKey(config.welcomeMessage) ? 'Using translation key' : 'Using literal text') : 'Default value'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'users' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-card-foreground mb-2">User Management</h2>
                <p className="text-muted-foreground">
                  Manage users, passwords, and authentication settings
                </p>
              </div>

              <div className="space-y-6">
                {/* Change Password Section */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-foreground">Change Password</h3>
                    <button
                      onClick={() => setShowChangePasswordForm(true)}
                      className="inline-flex items-center px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 text-sm"
                    >
                      <Key className="w-3 h-3 mr-1" />
                      Change Password
                    </button>
                  </div>
                </div>

                {/* Users List */}
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-foreground">Users</h3>
                  <button
                    onClick={handleCreateUser}
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add User
                  </button>
                </div>

                {usersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Loading users...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {users.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No users found. Click "Add User" to get started.
                      </div>
                    ) : (
                      users.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="p-2 bg-primary/10 rounded-full">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <h4 className="font-medium text-foreground">{user.name}</h4>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs">
                                  {user.role}
                                </span>
                                {!user.isActive && (
                                  <span className="px-2 py-1 bg-muted text-muted-foreground rounded text-xs">
                                    Inactive
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* User Groups List */}
                <div className="border-t border-border pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-foreground">User Groups</h3>
                    <button
                      onClick={handleCreateUserGroup}
                      className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add User Group
                    </button>
                  </div>

                  {userGroupsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span className="ml-2 text-muted-foreground">Loading user groups...</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {userGroups.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          No user groups found. Click "Add User Group" to get started.
                        </div>
                      ) : (
                        userGroups.map((group) => (
                          <div key={group.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                            <div className="flex items-center space-x-4">
                              <div className="p-2 bg-primary/10 rounded-full">
                                <Users className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <h4 className="font-medium text-foreground">{group.name}</h4>
                                {group.description && (
                                  <p className="text-sm text-muted-foreground">{group.description}</p>
                                )}
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-xs">
                                    {group.users?.length || 0} {(group.users?.length || 0) === 1 ? 'user' : 'users'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditUserGroup(group)}
                                className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                                title="Edit group"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUserGroup(group.id)}
                                className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors"
                                title="Delete group"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Local Login Configuration */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <User className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-medium text-foreground">Local Login Configuration</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="local-login-enabled"
                        checked={localLoginEnabled}
                        onChange={(e) => setLocalLoginEnabled(e.target.checked)}
                        className="rounded border-input"
                      />
                      <label htmlFor="local-login-enabled" className="text-sm font-medium text-foreground">
                        Enable Local User/Password Login
                      </label>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      When enabled, users can log in using email and password credentials stored locally.
                    </p>
                  </div>
                </div>

                {/* OpenID Configuration */}
                <div className="border-t border-border pt-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <Shield className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-medium text-foreground">OpenID Connect Configuration</h3>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="openid-enabled"
                        checked={openidConfig.enabled}
                        onChange={(e) => setOpenidConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                        className="rounded border-input"
                      />
                      <label htmlFor="openid-enabled" className="text-sm font-medium text-foreground">
                        Enable OpenID Connect
                      </label>
                    </div>

                    {openidConfig.enabled && (
                      <>
                        {/* Provider Branding */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Provider Branding</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Provider Name</label>
                              <input
                                type="text"
                                value={openidConfig.providerName}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, providerName: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="Azure AD, Okta, Auth0, etc."
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Logo/Image URL</label>
                              <input
                                type="url"
                                value={openidConfig.logoUrl}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, logoUrl: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="https://example.com/logo.png"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Client Credentials */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Client Credentials</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Client ID</label>
                              <input
                                type="text"
                                value={openidConfig.clientId}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, clientId: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="your-client-id"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Client Secret</label>
                              <input
                                type="password"
                                value={openidConfig.clientSecret}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="your-client-secret"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Endpoints */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Endpoints</h4>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Authority URL (Discovery Endpoint)</label>
                            <input
                              type="url"
                              value={openidConfig.authority}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, authority: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                              placeholder="https://your-identity-provider.com/.well-known/openid-configuration"
                            />
                            <p className="text-xs text-muted-foreground">Leave this filled if your provider supports OIDC discovery. Otherwise, specify individual endpoints below.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Authorize URL (Optional)</label>
                              <input
                                type="url"
                                value={openidConfig.authorizeUrl}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, authorizeUrl: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="https://provider.com/oauth2/authorize"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Token URL (Optional)</label>
                              <input
                                type="url"
                                value={openidConfig.tokenUrl}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, tokenUrl: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="https://provider.com/oauth2/token"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Profile/UserInfo URL (Optional)</label>
                            <input
                              type="url"
                              value={openidConfig.profileUrl}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, profileUrl: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                              placeholder="https://provider.com/oauth2/userinfo"
                            />
                          </div>
                        </div>

                        {/* Scope and User Mapping */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Scope and User Mapping</h4>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Scope</label>
                            <input
                              type="text"
                              value={openidConfig.scope}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, scope: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                              placeholder="openid profile email"
                            />
                            <p className="text-xs text-muted-foreground">Space-separated list of OAuth scopes</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">JSON Path to Email</label>
                              <input
                                type="text"
                                value={openidConfig.jsonPathEmail}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, jsonPathEmail: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                                placeholder="email"
                              />
                              <p className="text-xs text-muted-foreground">Path in user profile to email field (e.g., "email" or "user.email")</p>
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">JSON Path to Name</label>
                              <input
                                type="text"
                                value={openidConfig.jsonPathName}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, jsonPathName: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                                placeholder="name"
                              />
                              <p className="text-xs text-muted-foreground">Path in user profile to name field (e.g., "name" or "user.displayName")</p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">JSON Path to OIDC User ID</label>
                            <input
                              type="text"
                              value={openidConfig.jsonPathOidcUserId}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, jsonPathOidcUserId: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                              placeholder="sub"
                            />
                            <p className="text-xs text-muted-foreground">Path in user profile to OIDC user ID field (e.g., "sub", "oid", or "user_id"). This ID will be passed to the chat agent API.</p>
                          </div>
                        </div>

                        {/* Callback Configuration */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Callback Configuration</h4>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Redirect URI</label>
                            <input
                              type="url"
                              value={openidConfig.redirectUri}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, redirectUri: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                              placeholder="https://yourapp.com/callback"
                            />
                          </div>
                          {callbackUrl && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md">
                              <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">Generated Callback URL:</p>
                              <code className="text-xs text-blue-700 dark:text-blue-300 break-all">{callbackUrl}</code>
                              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">Use this URL when configuring your OpenID provider.</p>
                            </div>
                          )}
                        </div>

                        {/* Button Customization */}
                        <div className="bg-muted/30 p-4 rounded-lg space-y-4">
                          <h4 className="font-medium text-foreground">Login Button Customization</h4>
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Button Template (HTML)</label>
                            <textarea
                              value={openidConfig.buttonTemplate}
                              onChange={(e) => setOpenidConfig(prev => ({ ...prev, buttonTemplate: e.target.value }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm resize-vertical"
                              rows={3}
                              placeholder='<button class="{{classes}}"><img src="{{imageUrl}}" alt="{{providerName}}" /> Sign in with {{providerName}}</button>'
                            />
                            <p className="text-xs text-muted-foreground">Available variables: {'{{providerName}}'}, {'{{imageUrl}}'}, {'{{classes}}'}</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Button CSS Classes</label>
                              <input
                                type="text"
                                value={openidConfig.buttonClasses}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, buttonClasses: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                                placeholder="oidc-login-button btn-primary"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium text-foreground">Button Image URL</label>
                              <input
                                type="url"
                                value={openidConfig.buttonImageUrl}
                                onChange={(e) => setOpenidConfig(prev => ({ ...prev, buttonImageUrl: e.target.value }))}
                                className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                                placeholder="https://example.com/button-icon.png"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={handleSaveAuthConfig}
                        disabled={saving}
                        className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            Save Auth Config
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Agent Management Tab */}
          {activeTab === 'agents' && (
            <div className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground mb-2">Agent Management</h2>
                  <p className="text-muted-foreground">
                    Manage your AI assistants and their configurations
                  </p>
                </div>
                <button
                  onClick={handleCreateAgent}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Agent
                </button>
              </div>

              {agentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">Loading agents...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {agents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No agents configured yet. Click "Add Agent" to get started.
                    </div>
                  ) : (
                    agents.map((agent) => {
                      const IconComponent = getIconComponent(agent.icon);
                      // Get the default language translation or first available
                      const defaultLang = languages.find(l => l.isDefault);
                      const translation = agent.translations?.find((t: any) =>
                        defaultLang ? t.languageId === defaultLang.id : true
                      ) || agent.translations?.[0];
                      const displayName = translation?.name || 'Unnamed Agent';
                      const displayDescription = translation?.description || '';

                      return (
                        <div key={agent.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: `${agent.color}20`, borderColor: agent.color }}
                            >
                              <IconComponent className="w-5 h-5" style={{ color: agent.color }} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium text-foreground">{displayName}</h3>
                                {agent.isPublic && (
                                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                                    Public
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">{displayDescription}</p>
                              {agent.isPublic && (
                                <div className="mt-2 flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">Public URL:</span>
                                  <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                                    {window.location.origin}/public/{import.meta.env.VITE_TENANT_SLUG || 'default'}/{agentNameToSlug(agent.name)}
                                  </code>
                                  <button
                                    onClick={() => {
                                      const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
                                      const url = `${window.location.origin}/public/${tenantSlug}/${agentNameToSlug(agent.name)}`;
                                      navigator.clipboard.writeText(url);
                                      notifySuccess('Copied', 'Public URL copied to clipboard');
                                    }}
                                    className="text-xs text-primary hover:text-primary/80 underline"
                                    title="Copy URL"
                                  >
                                    Copy
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditAgent(agent)}
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                              title="Edit agent"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteAgent(agent.id)}
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors"
                              title="Delete agent"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* Embedded Content Tab */}
          {activeTab === 'embedded' && (
            <div className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground mb-2">Embedded Content</h2>
                  <p className="text-muted-foreground">
                    Manage embedded content like iframes, widgets, and external applications
                  </p>
                </div>
                <button
                  onClick={handleCreateEmbeddedContent}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Embedded Content
                </button>
              </div>

              {embeddedContentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <span className="ml-2 text-muted-foreground">Loading embedded contents...</span>
                </div>
              ) : (
                <div className="space-y-4">
                  {embeddedContents.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No embedded content configured yet. Click "Add Embedded Content" to get started.
                    </div>
                  ) : (
                    embeddedContents.map((embeddedContent) => {
                      const IconComponent = getIconComponent(embeddedContent.icon);
                      return (
                        <div key={embeddedContent.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div
                              className="p-2 rounded-lg"
                              style={{ backgroundColor: `${embeddedContent.color}20`, borderColor: embeddedContent.color }}
                            >
                              <IconComponent className="w-5 h-5" style={{ color: embeddedContent.color }} />
                            </div>
                            <div>
                              <h3 className="font-medium text-foreground">{embeddedContent.name}</h3>
                              <p className="text-sm text-muted-foreground">{embeddedContent.description}</p>
                              <p className="text-xs text-muted-foreground mt-1">{embeddedContent.embeddingUrl}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditEmbeddedContent(embeddedContent)}
                              className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                              title="Edit embedded content"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteEmbeddedContent(embeddedContent.id)}
                              className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors"
                              title="Delete embedded content"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          )}

          {/* Home Screen Tab */}
          {activeTab === 'home' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-card-foreground mb-2">Home Screen Setup</h2>
                <p className="text-muted-foreground">
                  Configure the home screen layout by dragging items from the toolbar to the canvas
                </p>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="grid grid-cols-12 gap-6">
                  {/* Toolbar */}
                  <div className="col-span-3">
                    <div className="bg-muted/30 p-4 rounded-lg">
                      <h3 className="font-medium text-foreground mb-4">Toolbar</h3>
                      <div className="space-y-3">
                        <DraggableToolbarItem id="toolbar-grouping-container">
                          <div className="flex items-center space-x-2">
                            <Folder className="w-4 h-4 text-primary" />
                            <span className="text-sm font-medium">Grouping Container</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">Group multiple items together</p>
                        </DraggableToolbarItem>

                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-foreground">Agents</h4>
                          {agents
                            .filter(agent => {
                              // Show agent in toolbar if it's not in home screen OR if it's referenced but the reference is broken
                              const inHomeScreen = homeScreenItems.some(item => item.type === 'agent' && item.agentId === agent.id);
                              return !inHomeScreen;
                            })
                            .map((agent) => {
                              const IconComponent = getIconComponent(agent.icon);
                              // Get the default language translation or first available
                              const defaultLang = languages.find(l => l.isDefault);
                              const translation = agent.translations?.find((t: any) =>
                                defaultLang ? t.languageId === defaultLang.id : true
                              ) || agent.translations?.[0];
                              const displayName = translation?.name || 'Unnamed Agent';

                              return (
                                <DraggableToolbarItem
                                  key={`toolbar-agent-${agent.id}`}
                                  id={`toolbar-agent-${agent.id}`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <IconComponent className="w-4 h-4" style={{ color: agent.color }} />
                                    <span className="text-sm">{displayName}</span>
                                  </div>
                                </DraggableToolbarItem>
                              );
                            })}
                          {agents.filter(agent => !homeScreenItems.some(item => item.type === 'agent' && item.agentId === agent.id)).length === 0 && agents.length > 0 && (
                            <div className="text-xs text-muted-foreground italic">All agents added</div>
                          )}
                          {agents.length === 0 && (
                            <div className="text-xs text-muted-foreground italic">No agents available</div>
                          )}
                        </div>

                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-foreground">Embedded Content</h4>
                          {embeddedContents
                            .filter(content => {
                              const inHomeScreen = homeScreenItems.some(item => item.type === 'embedded' && item.embeddedContentId === content.id);
                              return !inHomeScreen;
                            })
                            .map((embeddedContent) => {
                              const IconComponent = getIconComponent(embeddedContent.icon);
                              return (
                                <DraggableToolbarItem
                                  key={`toolbar-embedded-${embeddedContent.id}`}
                                  id={`toolbar-embedded-${embeddedContent.id}`}
                                >
                                  <div className="flex items-center space-x-2">
                                    <IconComponent className="w-4 h-4" style={{ color: embeddedContent.color }} />
                                    <span className="text-sm">{embeddedContent.name}</span>
                                  </div>
                                </DraggableToolbarItem>
                              );
                            })}
                          {embeddedContents.filter(content => !homeScreenItems.some(item => item.type === 'embedded' && item.embeddedContentId === content.id)).length === 0 && embeddedContents.length > 0 && (
                            <div className="text-xs text-muted-foreground italic">All content added</div>
                          )}
                          {embeddedContents.length === 0 && (
                            <div className="text-xs text-muted-foreground italic">No content available</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Canvas */}
                  <div className="col-span-9">
                    <div className="bg-muted/30 p-4 rounded-lg min-h-[400px]">
                      <h3 className="font-medium text-foreground mb-4">Home Screen Canvas</h3>
                      <DroppableCanvas id="home-screen-canvas" className="min-h-[350px]">
                        {homeScreenItems.filter(item => !item.parentId).length === 0 ? (
                          <div id="canvas-dropzone" className="text-center py-12 text-muted-foreground border-2 border-dashed border-border rounded-lg">
                            Drag items from the toolbar to start building your home screen
                          </div>
                        ) : (
                          <div className="flex flex-row flex-wrap gap-4">
                            {homeScreenItems.filter(item => !item.parentId).sort((a, b) => (a.order || 0) - (b.order || 0)).map((item) => {
                              if (item.type === 'grouping-container') {
                                const childItems = homeScreenItems.filter(child => child.parentId === item.id);
                                // Calculate grid size based on children + 1 space (4 columns max)
                                const maxColumns = 4;
                                const totalSpaces = childItems.length + 1; // +1 for drop space
                                const actualColumns = Math.min(totalSpaces, maxColumns);
                                const rows = Math.ceil(totalSpaces / maxColumns);

                                // Each item: 128px (w-32), gap: 12px (gap-3), padding: 16px (p-4) on each side
                                const itemWidth = 128;
                                const gapWidth = 12;
                                const padding = 32; // 16px * 2
                                const borderWidth = 4; // 2px * 2 for border-2
                                const calculatedWidth = (actualColumns * itemWidth) + ((actualColumns - 1) * gapWidth) + padding + borderWidth + 20;
                                const minHeight = Math.max(120, rows * 144); // 144px per row

                                // Get siblings for arrow buttons
                                const siblings = homeScreenItems.filter(i => i.parentId === item.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));
                                const itemIndex = siblings.findIndex(i => i.id === item.id);

                                return (
                                  <div key={item.id} style={{ width: `${calculatedWidth}px` }} className="relative">
                                    <DraggableCanvasItem id={item.id} className="z-0 w-full">
                                      <div className="relative">
                                        <div className="absolute -top-3 left-4 px-2 bg-background z-10 flex items-center space-x-2">
                                          <Folder className="w-5 h-5 text-primary" />
                                          <span className="font-medium text-lg">{item.showTitle !== false ? item.name : ''}</span>
                                        </div>
                                        <div className="absolute -top-2 -right-2 flex items-center gap-1 z-20">
                                          <button
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEditHomeScreenGroup(item);
                                            }}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="p-1.5 bg-background border border-border rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                                            title="Edit group settings"
                                          >
                                            <Edit className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={(e) => handleDeleteHomeScreenItem(e, item.id)}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="p-1.5 bg-background border border-border rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors"
                                            title="Remove from home screen"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                        <div className="border-2 border-border rounded-lg bg-background pb-4">
                                          <div style={{ minHeight: `${minHeight}px` }}>
                                            <DroppableCanvas
                                              id={`group-dropzone-${item.id}`}
                                              className="p-3"
                                            >
                                              {childItems.length === 0 ? (
                                                <div className="text-center py-8 text-sm text-muted-foreground">
                                                  Drop items here to add them to this group
                                                </div>
                                              ) : (
                                                <div className="flex flex-row flex-wrap gap-3">
                                                  {childItems.sort((a, b) => (a.order || 0) - (b.order || 0)).map((childItem) => {
                                                    if (childItem.type === 'agent') {
                                                      const agent = agents.find(a => a.id === childItem.agentId);
                                                      if (!agent) return null;
                                                      const IconComponent = getIconComponent(agent.icon);
                                                      // Get the default language translation or first available
                                                      const defaultLang = languages.find(l => l.isDefault);
                                                      const translation = agent.translations?.find((t: any) =>
                                                        defaultLang ? t.languageId === defaultLang.id : true
                                                      ) || agent.translations?.[0];
                                                      const displayName = translation?.name || 'Unnamed Agent';
                                                      const childSiblings = childItems.sort((a, b) => (a.order || 0) - (b.order || 0));
                                                      const childIndex = childSiblings.findIndex(i => i.id === childItem.id);
                                                      return (
                                                        <DraggableCanvasItem
                                                          key={childItem.id}
                                                          id={childItem.id}
                                                          className="relative w-32 h-32 z-10"
                                                        >
                                                          <div className="w-full h-full p-3 border border-border rounded-lg bg-background flex flex-col items-center justify-center text-center gap-2 hover:border-primary transition-colors">
                                                            <IconComponent className="w-8 h-8" style={{ color: agent.color }} />
                                                            <span className="text-sm font-medium line-clamp-2">{displayName}</span>
                                                          </div>
                                                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-background border border-border rounded-full p-0.5 z-20">
                                                            <button
                                                              onClick={(e) => handleReorderItem(e, childItem.id, 'left')}
                                                              disabled={childIndex === 0}
                                                              onPointerDown={(e) => e.stopPropagation()}
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                              title="Move left"
                                                            >
                                                              <ChevronLeft className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                              onClick={(e) => handleReorderItem(e, childItem.id, 'right')}
                                                              disabled={childIndex === childSiblings.length - 1}
                                                              onPointerDown={(e) => e.stopPropagation()}
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                              title="Move right"
                                                            >
                                                              <ChevronRight className="w-3 h-3" />
                                                            </button>
                                                          </div>
                                                          <button
                                                            onClick={(e) => handleDeleteHomeScreenItem(e, childItem.id)}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            className="absolute -top-2 -right-2 p-1 bg-background border border-border rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors z-20"
                                                            title="Remove item"
                                                          >
                                                            <Trash2 className="w-3 h-3" />
                                                          </button>
                                                        </DraggableCanvasItem>
                                                      );
                                                    } else if (childItem.type === 'embedded') {
                                                      const embeddedContent = embeddedContents.find(e => e.id === childItem.embeddedContentId);
                                                      if (!embeddedContent) return null;
                                                      const IconComponent = getIconComponent(embeddedContent.icon);
                                                      const childSiblings = childItems.sort((a, b) => (a.order || 0) - (b.order || 0));
                                                      const childIndex = childSiblings.findIndex(i => i.id === childItem.id);
                                                      return (
                                                        <DraggableCanvasItem
                                                          key={childItem.id}
                                                          id={childItem.id}
                                                          className="relative w-32 h-32 z-10"
                                                        >
                                                          <div className="w-full h-full p-3 border border-border rounded-lg bg-background flex flex-col items-center justify-center text-center gap-2 hover:border-primary transition-colors">
                                                            <IconComponent className="w-8 h-8" style={{ color: embeddedContent.color }} />
                                                            <span className="text-sm font-medium line-clamp-2">{embeddedContent.name}</span>
                                                          </div>
                                                          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-background border border-border rounded-full p-0.5 z-20">
                                                            <button
                                                              onClick={(e) => handleReorderItem(e, childItem.id, 'left')}
                                                              disabled={childIndex === 0}
                                                              onPointerDown={(e) => e.stopPropagation()}
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                              title="Move left"
                                                            >
                                                              <ChevronLeft className="w-3 h-3" />
                                                            </button>
                                                            <button
                                                              onClick={(e) => handleReorderItem(e, childItem.id, 'right')}
                                                              disabled={childIndex === childSiblings.length - 1}
                                                              onPointerDown={(e) => e.stopPropagation()}
                                                              onMouseDown={(e) => e.stopPropagation()}
                                                              className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                              title="Move right"
                                                            >
                                                              <ChevronRight className="w-3 h-3" />
                                                            </button>
                                                          </div>
                                                          <button
                                                            onClick={(e) => handleDeleteHomeScreenItem(e, childItem.id)}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            className="absolute -top-2 -right-2 p-1 bg-background border border-border rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors z-20"
                                                            title="Remove item"
                                                          >
                                                            <Trash2 className="w-3 h-3" />
                                                          </button>
                                                        </DraggableCanvasItem>
                                                      );
                                                    }
                                                    return null;
                                                  })}
                                                </div>
                                              )}
                                            </DroppableCanvas>
                                          </div>
                                        </div>
                                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-background border border-border rounded-full p-0.5 z-20">
                                          <button
                                            onClick={(e) => handleReorderItem(e, item.id, 'left')}
                                            disabled={itemIndex === 0}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Move left"
                                          >
                                            <ChevronLeft className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={(e) => handleReorderItem(e, item.id, 'right')}
                                            disabled={itemIndex === siblings.length - 1}
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Move right"
                                          >
                                            <ChevronRight className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </DraggableCanvasItem>
                                  </div>
                                );
                              } else if (item.type === 'agent') {
                                const agent = agents.find(a => a.id === item.agentId);
                                if (!agent) return null;
                                const IconComponent = getIconComponent(agent.icon);
                                // Get the default language translation or first available
                                const defaultLang = languages.find(l => l.isDefault);
                                const translation = agent.translations?.find((t: any) =>
                                  defaultLang ? t.languageId === defaultLang.id : true
                                ) || agent.translations?.[0];
                                const displayName = translation?.name || 'Unnamed Agent';
                                const siblings = homeScreenItems.filter(i => i.parentId === item.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));
                                const itemIndex = siblings.findIndex(i => i.id === item.id);
                                return (
                                  <DraggableCanvasItem
                                    key={item.id}
                                    id={item.id}
                                    className="relative w-40 h-40 z-10"
                                  >
                                    <div className="w-full h-full p-4 border-2 border-border rounded-lg bg-background flex flex-col items-center justify-center text-center gap-3 hover:border-primary transition-colors">
                                      <IconComponent className="w-10 h-10" style={{ color: agent.color }} />
                                      <span className="font-medium line-clamp-2">{displayName}</span>
                                    </div>
                                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-background border border-border rounded-full p-0.5 z-20">
                                      <button
                                        onClick={(e) => handleReorderItem(e, item.id, 'left')}
                                        disabled={itemIndex === 0}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move left"
                                      >
                                        <ChevronLeft className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={(e) => handleReorderItem(e, item.id, 'right')}
                                        disabled={itemIndex === siblings.length - 1}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move right"
                                      >
                                        <ChevronRight className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <button
                                      onClick={(e) => handleDeleteHomeScreenItem(e, item.id)}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="absolute -top-2 -right-2 p-1.5 bg-background border border-border rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors z-20"
                                      title="Remove from home screen"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </DraggableCanvasItem>
                                );
                              } else if (item.type === 'embedded') {
                                const embeddedContent = embeddedContents.find(e => e.id === item.embeddedContentId);
                                if (!embeddedContent) return null;
                                const IconComponent = getIconComponent(embeddedContent.icon);
                                const siblings = homeScreenItems.filter(i => i.parentId === item.parentId).sort((a, b) => (a.order || 0) - (b.order || 0));
                                const itemIndex = siblings.findIndex(i => i.id === item.id);
                                return (
                                  <DraggableCanvasItem
                                    key={item.id}
                                    id={item.id}
                                    className="relative w-40 h-40 z-10"
                                  >
                                    <div className="w-full h-full p-4 border-2 border-border rounded-lg bg-background flex flex-col items-center justify-center text-center gap-3 hover:border-primary transition-colors">
                                      <IconComponent className="w-10 h-10" style={{ color: embeddedContent.color }} />
                                      <span className="font-medium line-clamp-2">{embeddedContent.name}</span>
                                    </div>
                                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-background border border-border rounded-full p-0.5 z-20">
                                      <button
                                        onClick={(e) => handleReorderItem(e, item.id, 'left')}
                                        disabled={itemIndex === 0}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move left"
                                      >
                                        <ChevronLeft className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={(e) => handleReorderItem(e, item.id, 'right')}
                                        disabled={itemIndex === siblings.length - 1}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        className="p-0.5 text-muted-foreground hover:text-primary rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Move right"
                                      >
                                        <ChevronRight className="w-4 h-4" />
                                      </button>
                                    </div>
                                    <button
                                      onClick={(e) => handleDeleteHomeScreenItem(e, item.id)}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      className="absolute -top-2 -right-2 p-1.5 bg-background border border-border rounded-full text-muted-foreground hover:text-destructive hover:bg-muted transition-colors z-20"
                                      title="Remove from home screen"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </DraggableCanvasItem>
                                );
                              }
                              return null;
                            })}
                          </div>
                        )}
                      </DroppableCanvas>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={loadHomeScreenItems}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-border bg-background text-foreground rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Discard Changes
                  </button>
                  <button
                    onClick={handleSaveHomeScreen}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Home Screen
                      </>
                    )}
                  </button>
                </div>

                <DragOverlay dropAnimation={null}>
                  {activeId ? (() => {
                    if (activeId === 'toolbar-grouping-container') {
                      return (
                        <div className="p-3 bg-primary/10 rounded-lg shadow-lg w-fit" style={{ transform: 'translate(-50%, -50%)' }}>
                          <Folder className="w-8 h-8 text-primary" />
                        </div>
                      );
                    }

                    if (activeId.startsWith('toolbar-agent-')) {
                      const agentId = activeId.replace('toolbar-agent-', '');
                      const agent = agents.find(a => a.id === agentId);
                      if (agent) {
                        const IconComponent = getIconComponent(agent.icon);
                        return (
                          <div className="p-3 rounded-lg shadow-lg w-fit" style={{ backgroundColor: `${agent.color}20`, transform: 'translate(-50%, -50%)' }}>
                            <IconComponent className="w-8 h-8" style={{ color: agent.color }} />
                          </div>
                        );
                      }
                    }

                    if (activeId.startsWith('toolbar-embedded-')) {
                      const embeddedContentId = activeId.replace('toolbar-embedded-', '');
                      const embeddedContent = embeddedContents.find(e => e.id === embeddedContentId);
                      if (embeddedContent) {
                        const IconComponent = getIconComponent(embeddedContent.icon);
                        return (
                          <div className="p-3 rounded-lg shadow-lg w-fit" style={{ backgroundColor: `${embeddedContent.color}20`, transform: 'translate(-50%, -50%)' }}>
                            <IconComponent className="w-8 h-8" style={{ color: embeddedContent.color }} />
                          </div>
                        );
                      }
                    }

                    return null;
                  })() : null}
                </DragOverlay>
              </DndContext>
            </div>
          )}

          {/* i18n Management Tab */}
          {activeTab === 'i18n' && (
            <div className="p-6">
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-card-foreground mb-2">Internationalization (i18n)</h2>
                  <p className="text-muted-foreground">
                    Manage languages and translations for your application
                  </p>
                </div>
                <button
                  onClick={handleCreateLanguage}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Language
                </button>
              </div>

              <div className="space-y-6">
                {/* Languages List */}
                <div>
                  <h3 className="font-medium mb-3 text-foreground">Languages</h3>
                  <div className="space-y-2">
                    {languages.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground">
                        No languages configured yet. Click "Add Language" to get started.
                      </div>
                    ) : (
                      languages.map((language) => (
                        <div key={language.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                          <div>
                            <div className="font-medium text-foreground">{language.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {language.nativeName} ({language.code})
                              {language.isDefault && <span className="ml-2 px-2 py-1 bg-primary/10 text-primary rounded text-xs">Default</span>}
                              {!language.isActive && <span className="ml-2 px-2 py-1 bg-muted text-muted-foreground rounded text-xs">Inactive</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedLanguage(language.id);
                              loadTranslations(language.id);
                            }}
                            className={`px-3 py-1 rounded text-sm ${selectedLanguage === language.id
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                              }`}
                          >
                            {selectedLanguage === language.id ? 'Selected' : 'Select'}
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Translations */}
                {selectedLanguage && (
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-medium text-foreground">Translations</h3>
                      <button
                        onClick={() => setShowTranslationForm(true)}
                        className="inline-flex items-center px-3 py-1 bg-secondary text-secondary-foreground rounded hover:bg-secondary/80 text-sm"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Translation
                      </button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {translationList.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                          No translations yet. Add your first translation.
                        </div>
                      ) : (
                        translationList.map((translation) => (
                          <div key={translation.id} className="p-3 border border-border rounded-lg">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-mono text-sm text-primary mb-1">{translation.key}</div>
                                <div className="text-foreground">{translation.value}</div>
                                {translation.context && (
                                  <div className="text-xs text-muted-foreground mt-1">{translation.context}</div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Agent Form Modal */}
        {showAgentForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  {editingAgent ? 'Edit Agent' : 'Create New Agent'}
                </h3>
                <button
                  onClick={() => setShowAgentForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Language Selector */}
                {languages.length > 0 && (
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
                    <label className="text-sm font-medium text-foreground">Translation Language</label>
                    <div className="flex flex-wrap gap-2">
                      {languages.filter(l => l.isActive).map((language) => (
                        <button
                          key={language.id}
                          type="button"
                          onClick={() => setSelectedAgentLanguage(language.id)}
                          className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${selectedAgentLanguage === language.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                        >
                          {language.name}
                          {language.isDefault && <span className="ml-1 text-xs">(Default)</span>}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Select a language to edit the translatable fields below
                    </p>
                  </div>
                )}

                {/* Translatable Fields */}
                <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-amber-900 dark:text-amber-100">Translatable Fields</span>
                    <span className="text-xs text-amber-700 dark:text-amber-300">(Language-specific)</span>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      Name
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={agentForm.translations[selectedAgentLanguage]?.name || ''}
                      onChange={(e) => setAgentForm(prev => ({
                        ...prev,
                        translations: {
                          ...prev.translations,
                          [selectedAgentLanguage]: {
                            ...prev.translations[selectedAgentLanguage],
                            name: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border-2 border-red-300 bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Agent name"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      Description
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={agentForm.translations[selectedAgentLanguage]?.description || ''}
                      onChange={(e) => setAgentForm(prev => ({
                        ...prev,
                        translations: {
                          ...prev.translations,
                          [selectedAgentLanguage]: {
                            ...prev.translations[selectedAgentLanguage],
                            description: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border-2 border-red-300 bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                      rows={3}
                      placeholder="Brief description of the agent"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      Chat Title
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={agentForm.translations[selectedAgentLanguage]?.chatTitle || ''}
                      onChange={(e) => setAgentForm(prev => ({
                        ...prev,
                        translations: {
                          ...prev.translations,
                          [selectedAgentLanguage]: {
                            ...prev.translations[selectedAgentLanguage],
                            chatTitle: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border-2 border-red-300 bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Chat window title"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      Welcome Message
                    </label>
                    <textarea
                      value={agentForm.translations[selectedAgentLanguage]?.welcomeMessage || ''}
                      onChange={(e) => setAgentForm(prev => ({
                        ...prev,
                        translations: {
                          ...prev.translations,
                          [selectedAgentLanguage]: {
                            ...prev.translations[selectedAgentLanguage],
                            welcomeMessage: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                      rows={2}
                      placeholder="Message shown when chat starts"
                    />
                  </div>
                </div>

                {/* Non-translatable Fields */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-foreground">General Settings</span>
                    <span className="text-xs text-muted-foreground">(Not translated)</span>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-2">
                      Agent Name (Internal)
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={agentForm.name}
                      onChange={(e) => setAgentForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border-2 border-red-300 bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Internal identifier for the agent"
                    />
                    <p className="text-xs text-muted-foreground">
                      This is the internal name used to identify the agent in the system (not shown to users)
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Icon</label>
                      <select
                        value={agentForm.icon}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, icon: e.target.value as IconName }))}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      >
                        {iconOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Color</label>
                      <input
                        type="color"
                        value={agentForm.color}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, color: e.target.value }))}
                        className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Chat API URL</label>
                    <input
                      type="url"
                      value={agentForm.chatApiUrl}
                      onChange={(e) => setAgentForm(prev => ({ ...prev, chatApiUrl: e.target.value }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="https://api.example.com/chat"
                    />
                  </div>

                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <h4 className="text-base font-medium text-foreground">reCAPTCHA Configuration</h4>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.recaptchaEnabled}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, recaptchaEnabled: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable reCAPTCHA</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Require Google reCAPTCHA before chat messages are sent.
                      </p>
                    </div>

                    {agentForm.recaptchaEnabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <div className="space-y-2 md:col-span-2">
                          <label className="text-sm font-medium text-foreground">reCAPTCHA Site Key</label>
                          <input
                            type="text"
                            value={agentForm.recaptchaSiteKey}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, recaptchaSiteKey: e.target.value }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            placeholder="Google reCAPTCHA site key"
                          />
                          <p className="text-xs text-muted-foreground">
                            Public site key used by the chat widget. Secret keys should stay on the backend only.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">reCAPTCHA Version</label>
                          <select
                            value={agentForm.recaptchaVersion}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, recaptchaVersion: e.target.value as 'v2' | 'v3' }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                          >
                            <option value="v2">v2 checkbox</option>
                            <option value="v3">v3 invisible</option>
                          </select>
                          <p className="text-xs text-muted-foreground">
                            v2 shows a verification gate. v3 runs invisibly and attaches a score token to each message.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Agent SID Source</label>
                    <select
                      value={agentForm.agentSidSource}
                      onChange={(e) => setAgentForm(prev => ({ ...prev, agentSidSource: e.target.value as 'oidc_user_id' | 'user_id' | 'user_email' | 'custom' }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    >
                      <option value="user_email">User email</option>
                      <option value="user_id">User Id</option>
                      {openidConfig.enabled && <option value="oidc_user_id">OIDC User ID</option>}
                      <option value="custom">Custom</option>
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Choose what value will be passed to the chat API as agent_sid
                    </p>
                  </div>

                  {agentForm.agentSidSource === 'custom' && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Agent SID</label>
                      <input
                        type="text"
                        value={agentForm.agentSid}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, agentSid: e.target.value }))}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                        placeholder="Agent identifier"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Chat User ID <span className="text-xs text-muted-foreground">(Optional)</span></label>
                    <input
                      type="text"
                      value={agentForm.chatUserId}
                      onChange={(e) => setAgentForm(prev => ({ ...prev, chatUserId: e.target.value }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Fixed user ID (overrides Agent SID)"
                    />
                    <p className="text-xs text-muted-foreground">When set, this will override the Agent SID for chat sessions</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.isPublic}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, isPublic: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm font-medium text-foreground">Make agent public</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        When enabled, this agent can be accessed via a public URL without authentication.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Status</label>
                      <select
                        value={agentForm.isActive.toString()}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-col space-y-4 pt-4">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.streamingEnabled}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, streamingEnabled: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable streaming</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Enable real-time streaming of agent responses. Messages will appear progressively as they are generated.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.enablePersistence}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, enablePersistence: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable persistence</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Save chat history to browser storage. Users can resume conversations after closing and reopening the chat.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.enablePreemptiveEndChatButton}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, enablePreemptiveEndChatButton: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable preemptive end chat button</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Show an end chat button that allows users to proactively terminate the conversation. When clicked, sends a "preemptive-end-chat" intent to your API.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.enableClearChatButton}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, enableClearChatButton: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable clear chat button</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Show a button that allows users to clear the conversation and start fresh.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.showRouteStack}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, showRouteStack: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Show route stack</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Display a navigation indicator showing the current route/agent and allowing users to navigate back through the conversation history when using route intents.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.fetchForWelcomeMessage}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, fetchForWelcomeMessage: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Fetch for welcome message</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Automatically fetch the welcome message from your API on load by sending a "welcome-message-request" intent. Takes priority over static welcome message. The fetched message is stored and replayed when navigating back to this route.
                      </p>
                    </div>
                  </div>

                  {/* File Upload Configuration */}
                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-base font-medium text-foreground">File Upload Configuration</h4>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.allowFileUpload}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, allowFileUpload: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Enable attach button (file upload)</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Shows the paperclip icon in chat input and lets users attach files (sent as base64 in the "file-upload-from-chat" intent).
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.allowImagePaste}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, allowImagePaste: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Allow image paste from clipboard</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Allows pasting images directly into the chat input (even without clicking attach).
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">Allowed file types</label>
                      <input
                        type="text"
                        value={agentForm.allowedFileTypes}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, allowedFileTypes: e.target.value }))}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                        placeholder="pdf,docx,xlsx,jpg,png"
                      />
                      <p className="text-xs text-muted-foreground">
                        Comma-separated extensions. Leave empty to allow all types.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">Max file size (MB)</label>
                        <input
                          type="number"
                          min="1"
                          max="500"
                          value={agentForm.maxFileSize}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, maxFileSize: parseInt(e.target.value) || 10 }))}
                          className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">Max files per message</label>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          value={agentForm.maxFiles}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, maxFiles: parseInt(e.target.value) || 5 }))}
                          className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notification Configuration */}
                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <h4 className="text-base font-medium text-foreground">Notification Configuration</h4>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.allowNotifications}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, allowNotifications: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Allow notifications</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Enable the notification system for this agent. When enabled, users can receive real-time notifications from the agent.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={agentForm.fetchForNotification}
                          onChange={(e) => setAgentForm(prev => ({ ...prev, fetchForNotification: e.target.checked }))}
                          className="rounded border-input"
                        />
                        <span className="text-sm text-foreground">Fetch for notification</span>
                      </label>
                      <p className="text-xs text-muted-foreground ml-6">
                        Automatically poll the API for new notifications. When enabled, the agent will periodically check for updates.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Notification Polling Frequency (seconds)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="300"
                        value={agentForm.notificationPoolingFrequency}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, notificationPoolingFrequency: parseInt(e.target.value) || 15 }))}
                        className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                        placeholder="15"
                      />
                      <p className="text-xs text-muted-foreground">
                        How often to poll for new notifications (in seconds). Default is 15 seconds. Range: 1-300 seconds.
                      </p>
                    </div>
                  </div>

                  {/* Feedback Configuration */}
                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <h4 className="text-base font-medium text-foreground">Feedback Configuration</h4>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="feedback-enabled"
                        checked={agentForm.feedbackEnabled}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, feedbackEnabled: e.target.checked }))}
                        className="rounded border-input"
                      />
                      <label htmlFor="feedback-enabled" className="text-sm font-medium text-foreground">
                        Enable feedback buttons
                      </label>
                    </div>

                    {agentForm.feedbackEnabled && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            Positive Feedback URL
                            <span className="text-xs text-muted-foreground ml-1">(Optional)</span>
                          </label>
                          <input
                            type="url"
                            value={agentForm.feedbackPositiveUrl}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, feedbackPositiveUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            placeholder="https://api.example.com/feedback/positive"
                          />
                          <p className="text-xs text-muted-foreground">Webhook URL for positive/thumbs up feedback</p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            Negative Feedback URL
                            <span className="text-xs text-muted-foreground ml-1">(Optional)</span>
                          </label>
                          <input
                            type="url"
                            value={agentForm.feedbackNegativeUrl}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, feedbackNegativeUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            placeholder="https://api.example.com/feedback/negative"
                          />
                          <p className="text-xs text-muted-foreground">Webhook URL for negative/thumbs down feedback</p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            Minimum Characters for Negative Feedback
                          </label>
                          <input
                            type="number"
                            min={1}
                            max={200}
                            value={agentForm.feedbackMinChars}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, feedbackMinChars: parseInt(e.target.value) || 20 }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            placeholder="20"
                          />
                          <p className="text-xs text-muted-foreground">Minimum number of characters required for negative feedback comments (default: 20)</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footnote Configuration */}
                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <h4 className="text-base font-medium text-foreground">Footnote Configuration</h4>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="footnote-enabled"
                        checked={agentForm.footnoteEnabled}
                        onChange={(e) => setAgentForm(prev => ({ ...prev, footnoteEnabled: e.target.checked }))}
                        className="rounded border-input"
                      />
                      <label htmlFor="footnote-enabled" className="text-sm font-medium text-foreground">
                        Enable message footnotes
                      </label>
                    </div>

                    {agentForm.footnoteEnabled && (
                      <div className="space-y-4 pl-6">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            Footnote Content
                            <span className="text-xs text-muted-foreground ml-1">(Required)</span>
                          </label>
                          <textarea
                            value={agentForm.agentMessageFootnote}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, agentMessageFootnote: e.target.value }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                            rows={3}
                            placeholder="Enter the footnote text that will appear below agent messages..."
                          />
                          <p className="text-xs text-muted-foreground">This text will be displayed below agent responses</p>
                        </div>

                        <div className="space-y-2">
                          <label className="text-sm font-medium text-foreground">
                            Footnote Title
                            <span className="text-xs text-muted-foreground ml-1">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            value={agentForm.footnoteTitle}
                            onChange={(e) => setAgentForm(prev => ({ ...prev, footnoteTitle: e.target.value }))}
                            className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            placeholder="e.g., Disclaimer, Note, Important"
                          />
                          <p className="text-xs text-muted-foreground">Optional heading for the footnote section</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-foreground">Display Timing</label>
                            <select
                              value={agentForm.footnoteShowTiming}
                              onChange={(e) => setAgentForm(prev => ({ ...prev, footnoteShowTiming: e.target.value as 'after_response' | 'immediately' | 'let_agent_decide' }))}
                              className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                            >
                              <option value="after_response">After response completes</option>
                              <option value="immediately">Show immediately</option>
                              <option value="let_agent_decide">Let agent decide</option>
                            </select>
                            <p className="text-xs text-muted-foreground">When to display the footnote</p>
                          </div>

                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id="footnote-collapsible"
                              checked={agentForm.footnoteCollapsible}
                              onChange={(e) => setAgentForm(prev => ({ ...prev, footnoteCollapsible: e.target.checked }))}
                              className="rounded border-input"
                            />
                            <label htmlFor="footnote-collapsible" className="text-sm font-medium text-foreground">
                              Make footnote collapsible
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Theme Configuration */}
                  <div className="border-t border-border pt-6 space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <h4 className="text-base font-medium text-foreground">Custom Theme Colors</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Override the default theme colors for this agent. Leave empty to use default theme.
                    </p>

                    {/* Light Theme */}
                    <details className="border border-border rounded-lg">
                      <summary className="cursor-pointer p-3 hover:bg-muted/50 transition-colors font-medium text-foreground">
                        Light Theme Colors
                      </summary>
                      <div className="p-4 space-y-4 border-t border-border">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ColorPickerInput
                            label="Primary"
                            value={agentForm.themePrimary}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themePrimary: value }))}
                          />
                          <ColorPickerInput
                            label="Primary Foreground"
                            value={agentForm.themePrimaryForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themePrimaryForeground: value }))}
                            placeholder="e.g., 222.2 47.4% 11.2%"
                            defaultColor="222 47% 11%"
                          />
                          <ColorPickerInput
                            label="Secondary"
                            value={agentForm.themeSecondary}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeSecondary: value }))}
                            placeholder="e.g., 210 40% 96.1%"
                            defaultColor="210 40% 96%"
                          />
                          <ColorPickerInput
                            label="Secondary Foreground"
                            value={agentForm.themeSecondaryForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeSecondaryForeground: value }))}
                            placeholder="e.g., 222.2 47.4% 11.2%"
                            defaultColor="222 47% 11%"
                          />
                          <ColorPickerInput
                            label="Background"
                            value={agentForm.themeBackground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeBackground: value }))}
                            placeholder="e.g., 0 0% 100%"
                            defaultColor="0 0% 100%"
                          />
                          <ColorPickerInput
                            label="Foreground"
                            value={agentForm.themeForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeForeground: value }))}
                            placeholder="e.g., 222.2 84% 4.9%"
                            defaultColor="222 84% 5%"
                          />
                          <ColorPickerInput
                            label="Card"
                            value={agentForm.themeCard}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeCard: value }))}
                            placeholder="e.g., 0 0% 100%"
                            defaultColor="0 0% 100%"
                          />
                          <ColorPickerInput
                            label="Card Foreground"
                            value={agentForm.themeCardForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeCardForeground: value }))}
                            placeholder="e.g., 222.2 84% 4.9%"
                            defaultColor="222 84% 5%"
                          />
                          <ColorPickerInput
                            label="Muted"
                            value={agentForm.themeMuted}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeMuted: value }))}
                            placeholder="e.g., 210 40% 96.1%"
                            defaultColor="210 40% 96%"
                          />
                          <ColorPickerInput
                            label="Muted Foreground"
                            value={agentForm.themeMutedForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeMutedForeground: value }))}
                            placeholder="e.g., 215.4 16.3% 46.9%"
                            defaultColor="215 16% 47%"
                          />
                          <ColorPickerInput
                            label="Border"
                            value={agentForm.themeBorder}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeBorder: value }))}
                            placeholder="e.g., 214.3 31.8% 91.4%"
                            defaultColor="214 32% 91%"
                          />
                          <ColorPickerInput
                            label="Input"
                            value={agentForm.themeInput}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeInput: value }))}
                            placeholder="e.g., 214.3 31.8% 91.4%"
                            defaultColor="214 32% 91%"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Colors should be in HSL format without the 'hsl()' wrapper. Example: "210 40% 98%"
                        </p>
                      </div>
                    </details>

                    {/* Dark Theme */}
                    <details className="border border-border rounded-lg">
                      <summary className="cursor-pointer p-3 hover:bg-muted/50 transition-colors font-medium text-foreground">
                        Dark Theme Colors
                      </summary>
                      <div className="p-4 space-y-4 border-t border-border">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <ColorPickerInput
                            label="Primary"
                            value={agentForm.themeDarkPrimary}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkPrimary: value }))}
                            defaultColor="210 40% 98%"
                          />
                          <ColorPickerInput
                            label="Primary Foreground"
                            value={agentForm.themeDarkPrimaryForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkPrimaryForeground: value }))}
                            placeholder="e.g., 222.2 47.4% 11.2%"
                            defaultColor="222 47% 11%"
                          />
                          <ColorPickerInput
                            label="Secondary"
                            value={agentForm.themeDarkSecondary}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkSecondary: value }))}
                            placeholder="e.g., 217.2 32.6% 17.5%"
                            defaultColor="217 33% 18%"
                          />
                          <ColorPickerInput
                            label="Secondary Foreground"
                            value={agentForm.themeDarkSecondaryForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkSecondaryForeground: value }))}
                            defaultColor="210 40% 98%"
                          />
                          <ColorPickerInput
                            label="Background"
                            value={agentForm.themeDarkBackground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkBackground: value }))}
                            placeholder="e.g., 222.2 84% 4.9%"
                            defaultColor="222 84% 5%"
                          />
                          <ColorPickerInput
                            label="Foreground"
                            value={agentForm.themeDarkForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkForeground: value }))}
                            defaultColor="210 40% 98%"
                          />
                          <ColorPickerInput
                            label="Card"
                            value={agentForm.themeDarkCard}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkCard: value }))}
                            placeholder="e.g., 222.2 84% 4.9%"
                            defaultColor="222 84% 5%"
                          />
                          <ColorPickerInput
                            label="Card Foreground"
                            value={agentForm.themeDarkCardForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkCardForeground: value }))}
                            defaultColor="210 40% 98%"
                          />
                          <ColorPickerInput
                            label="Muted"
                            value={agentForm.themeDarkMuted}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkMuted: value }))}
                            placeholder="e.g., 217.2 32.6% 17.5%"
                            defaultColor="217 33% 18%"
                          />
                          <ColorPickerInput
                            label="Muted Foreground"
                            value={agentForm.themeDarkMutedForeground}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkMutedForeground: value }))}
                            placeholder="e.g., 215 20.2% 65.1%"
                            defaultColor="215 20% 65%"
                          />
                          <ColorPickerInput
                            label="Border"
                            value={agentForm.themeDarkBorder}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkBorder: value }))}
                            placeholder="e.g., 217.2 32.6% 17.5%"
                            defaultColor="217 33% 18%"
                          />
                          <ColorPickerInput
                            label="Input"
                            value={agentForm.themeDarkInput}
                            onChange={(value) => setAgentForm(prev => ({ ...prev, themeDarkInput: value }))}
                            placeholder="e.g., 217.2 32.6% 17.5%"
                            defaultColor="217 33% 18%"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Colors should be in HSL format without the 'hsl()' wrapper. Example: "217.2 32.6% 17.5%"
                        </p>
                      </div>
                    </details>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowAgentForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAgent}
                    disabled={
                      saving ||
                      !agentForm.name.trim() ||
                      !selectedAgentLanguage ||
                      !agentForm.translations[selectedAgentLanguage]?.name?.trim()
                    }
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingAgent ? 'Update Agent' : 'Create Agent'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Embedded Content Form Modal */}
        {showEmbeddedContentForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  {editingEmbeddedContent ? 'Edit Embedded Content' : 'Create New Embedded Content'}
                </h3>
                <button
                  onClick={() => setShowEmbeddedContentForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Name</label>
                    <input
                      type="text"
                      value={embeddedContentForm.name}
                      onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Embedded content name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Icon</label>
                    <select
                      value={embeddedContentForm.icon}
                      onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, icon: e.target.value as IconName }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    >
                      {iconOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Description</label>
                  <textarea
                    value={embeddedContentForm.description}
                    onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                    rows={3}
                    placeholder="Brief description of the embedded content"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Color</label>
                    <input
                      type="color"
                      value={embeddedContentForm.color}
                      onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, color: e.target.value }))}
                      className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Status</label>
                    <select
                      value={embeddedContentForm.isActive.toString()}
                      onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Embedding URL</label>
                  <input
                    type="url"
                    value={embeddedContentForm.embeddingUrl}
                    onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, embeddingUrl: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="https://example.com/embed"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Title</label>
                  <input
                    type="text"
                    value={embeddedContentForm.title}
                    onChange={(e) => setEmbeddedContentForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Display title for the embedded content"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowEmbeddedContentForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEmbeddedContent}
                    disabled={saving || !embeddedContentForm.name.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingEmbeddedContent ? 'Update Embedded Content' : 'Create Embedded Content'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Language Form Modal */}
        {showLanguageForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  Add New Language
                </h3>
                <button
                  onClick={() => setShowLanguageForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Language Code</label>
                  <input
                    type="text"
                    value={languageForm.code}
                    onChange={(e) => setLanguageForm(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="en-US"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Display Name</label>
                  <input
                    type="text"
                    value={languageForm.name}
                    onChange={(e) => setLanguageForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="English (US)"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Native Name</label>
                  <input
                    type="text"
                    value={languageForm.nativeName}
                    onChange={(e) => setLanguageForm(prev => ({ ...prev, nativeName: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="English"
                  />
                </div>

                <div className="flex items-center space-x-4 pt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={languageForm.isActive}
                      onChange={(e) => setLanguageForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-foreground">Active</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={languageForm.isDefault}
                      onChange={(e) => setLanguageForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                      className="rounded border-input"
                    />
                    <span className="text-sm text-foreground">Default Language</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowLanguageForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveLanguage}
                    disabled={saving || !languageForm.code.trim() || !languageForm.name.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Create Language
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Translation Form Modal */}
        {showTranslationForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  Add Translation
                </h3>
                <button
                  onClick={() => setShowTranslationForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Translation Key</label>
                  <input
                    type="text"
                    value={translationForm.key}
                    onChange={(e) => setTranslationForm(prev => ({ ...prev, key: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground font-mono text-sm"
                    placeholder="common.save"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Translated Text</label>
                  <textarea
                    value={translationForm.value}
                    onChange={(e) => setTranslationForm(prev => ({ ...prev, value: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                    rows={3}
                    placeholder="Save"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Context (Optional)</label>
                  <input
                    type="text"
                    value={translationForm.context}
                    onChange={(e) => setTranslationForm(prev => ({ ...prev, context: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Description for translators"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowTranslationForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveTranslation}
                    disabled={saving || !translationForm.key.trim() || !translationForm.value.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Translation
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Form Modal */}
        {showUserForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <button
                  onClick={() => setShowUserForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Name</label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="User name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Email</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="user@example.com"
                  />
                </div>
                {!editingUser && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Password</label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                      placeholder="Password"
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Role</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="user-active"
                    checked={userForm.isActive}
                    onChange={(e) => setUserForm(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-input"
                  />
                  <label htmlFor="user-active" className="text-sm text-foreground">Active</label>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowUserForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveUser}
                    disabled={saving || !userForm.name.trim() || !userForm.email.trim() || (!editingUser && !userForm.password.trim())}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Modal */}
        {showChangePasswordForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  Change Password
                </h3>
                <button
                  onClick={() => setShowChangePasswordForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Current Password</label>
                  <input
                    type="password"
                    value={changePasswordForm.currentPassword}
                    onChange={(e) => setChangePasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Current password"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">New Password</label>
                  <input
                    type="password"
                    value={changePasswordForm.newPassword}
                    onChange={(e) => setChangePasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="New password"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Confirm New Password</label>
                  <input
                    type="password"
                    value={changePasswordForm.confirmPassword}
                    onChange={(e) => setChangePasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Confirm new password"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowChangePasswordForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={saving}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Change Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Group Form Modal */}
        {showUserGroupForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  {editingUserGroup ? 'Edit User Group' : 'Create New User Group'}
                </h3>
                <button
                  onClick={() => setShowUserGroupForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Group Name</label>
                  <input
                    type="text"
                    value={userGroupForm.name}
                    onChange={(e) => setUserGroupForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Group name"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Description (Optional)</label>
                  <textarea
                    value={userGroupForm.description}
                    onChange={(e) => setUserGroupForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground resize-vertical"
                    rows={3}
                    placeholder="Brief description of the group"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Users</label>
                  <div className="border border-input rounded-md bg-background p-3 max-h-60 overflow-y-auto space-y-2">
                    {users.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No users available</p>
                    ) : (
                      users.map((user) => (
                        <label key={user.id} className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={userGroupForm.userIds.includes(user.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setUserGroupForm(prev => ({ ...prev, userIds: [...prev.userIds, user.id] }));
                              } else {
                                setUserGroupForm(prev => ({ ...prev, userIds: prev.userIds.filter(id => id !== user.id) }));
                              }
                            }}
                            className="rounded border-input"
                          />
                          <span className="text-sm text-foreground">{user.name} ({user.email})</span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {userGroupForm.userIds.length} user(s) selected
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowUserGroupForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveUserGroup}
                    disabled={saving || !userGroupForm.name.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingUserGroup ? 'Update Group' : 'Create Group'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Home Screen Group Edit Modal */}
        {showGroupEditForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="bg-card border border-border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-border flex justify-between items-center">
                <h3 className="text-lg font-semibold text-card-foreground">
                  Edit Group Settings
                </h3>
                <button
                  onClick={() => setShowGroupEditForm(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  ×
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Group Name</label>
                  <input
                    type="text"
                    value={groupEditForm.name}
                    onChange={(e) => setGroupEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
                    placeholder="Group name"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="show-title"
                    checked={groupEditForm.showTitle}
                    onChange={(e) => setGroupEditForm(prev => ({ ...prev, showTitle: e.target.checked }))}
                    className="rounded border-input"
                  />
                  <label htmlFor="show-title" className="text-sm font-medium text-foreground">
                    Show group title
                  </label>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Group Access</label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Select which user groups can see this in the home screen. Leave empty to allow all users.
                  </p>
                  <div className="border border-input rounded-md bg-background p-3 max-h-60 overflow-y-auto space-y-2">
                    {userGroupsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading user groups...
                      </div>
                    ) : userGroups.length === 0 ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">No user groups available</p>

                        {/* If we have selected IDs but no groups loaded/available, show removable chips */}
                        {groupEditForm.groupAccess.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">Selected (not loaded)</p>
                            <div className="flex flex-wrap gap-2">
                              {groupEditForm.groupAccess.map((id) => (
                                <button
                                  key={id}
                                  type="button"
                                  onClick={() => setGroupEditForm(prev => ({ ...prev, groupAccess: prev.groupAccess.filter(gid => gid !== id) }))}
                                  className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-border bg-muted text-foreground text-xs hover:bg-muted/80"
                                  title="Remove this group from access list"
                                >
                                  <span className="font-mono">{id}</span>
                                  <span aria-hidden="true">×</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={loadUserGroups}
                          className="text-xs text-primary hover:text-primary/80 underline"
                        >
                          Refresh user groups
                        </button>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const knownIds = new Set(userGroups.map((g: any) => g.id));
                          const unknownSelected = groupEditForm.groupAccess.filter((id) => !knownIds.has(id));

                          return (
                            <>
                              {unknownSelected.length > 0 && (
                                <div className="p-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800">
                                  <p className="text-xs text-amber-900 dark:text-amber-100 font-medium mb-2">
                                    Some selected groups are no longer available
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {unknownSelected.map((id) => (
                                      <button
                                        key={id}
                                        type="button"
                                        onClick={() => setGroupEditForm(prev => ({ ...prev, groupAccess: prev.groupAccess.filter(gid => gid !== id) }))}
                                        className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-amber-300 bg-background text-foreground text-xs hover:bg-muted/80"
                                        title="Remove this missing group from access list"
                                      >
                                        <span className="font-mono">{id}</span>
                                        <span aria-hidden="true">×</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {userGroups.map((group: any) => (
                                <label key={group.id} className="flex items-center space-x-2 p-2 hover:bg-muted rounded cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={groupEditForm.groupAccess.includes(group.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGroupEditForm(prev => ({ ...prev, groupAccess: [...prev.groupAccess, group.id] }));
                                      } else {
                                        setGroupEditForm(prev => ({ ...prev, groupAccess: prev.groupAccess.filter(id => id !== group.id) }));
                                      }
                                    }}
                                    className="rounded border-input"
                                  />
                                  <span className="text-sm text-foreground">{group.name}</span>
                                </label>
                              ))}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {groupEditForm.groupAccess.length === 0
                      ? 'All users can see this group'
                      : `${groupEditForm.groupAccess.length} group(s) selected`}
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-border">
                  <button
                    onClick={() => setShowGroupEditForm(false)}
                    className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveHomeScreenGroup}
                    disabled={saving || !groupEditForm.name.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Settings
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
